/**
 * PageBuilderWorkflow — phased view of the page-builder agent's output.
 *
 * Single page-builder surface for a managed page: read-only stepper across
 * all phases (research evidence, framing, outline, body, ground, final),
 * per-phase Run buttons + chat, and stale invalidation.
 */
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WORKSPACE_TITLE_CLASS } from '../components/common/PageHeader';
import type { ReactNode } from 'react';
import { diffWords } from 'diff';
import PathChips from '../components/common/PathChips';
import { isNestedSiteType, previewPath } from '../components/common/pathRules';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useTheme } from '../contexts/ThemeContext';
import SiblingPagesSidebar from '../components/SiblingPagesSidebar';
import SEOPanel from '../components/SEOPanel';
import ProvenancePanel from '../components/ProvenancePanel';
import StructuredBodyEditor from '../components/editor/StructuredBodyEditor';
import PublishedBodyPreview from '../components/editor/PublishedBodyPreview';
import VersionDiff from '../components/VersionDiff';
import StageDiff from '../components/StageDiff';
import Breadcrumb, { BreadcrumbItem } from '../components/Breadcrumb';
import StageModelsWidget from '../components/common/StageModelsWidget';
import Internals from '../components/common/Internals';
import { useShowInternals } from '../contexts/InternalsContext';
import SerpModal from '../components/SerpModal';
import BuildContextStrip from '../components/page-builder/BuildContextStrip';
import ReturnTrail from '../components/common/ReturnTrail';
import { keepReturnTo, withReturnTo } from '../components/common/returnPath';
import PageBuilderChatPane from '../components/page-builder/PageBuilderChatPane';
import PhaseRunSplitButton from '../components/page-builder/PhaseRunSplitButton';
import type { RunMenuItem } from '../components/page-builder/PhaseRunSplitButton';
import { EngineIcon, formatSerpDate, RefreshGlyph } from '../components/common/SerpHelpers';
import { useNetworkEnabled } from '../hooks/useNetworkEnabled';
import type {
  Angle,
  AngleOption,
  BuildStateResponse,
  BuildPhase,
  CrawlReadiness,
  FramingArtifact,
  HeroImageState,
  InjectionMapEntry,
  PendingSource,
  PhaseStatus,
  ResearchEvidenceEntry,
  ResearchPoint,
  WebSocketEvent,
} from '../types';
import { KEYWORD_SHAPES } from '../types';

// While a SERP crawl is pending we poll the backend at this interval so the
// "Pending" state reflects reality (clears when the crawl finishes or its
// request goes stale) without a page reload. The serp_completion websocket
// event is the fast path; this is the backstop after a reload / worker death.
const SERP_PENDING_POLL_MS = 20 * 1000;

// Short copy describing each keyword_shape, shown next to the select so
// reviewers know what they're picking between without leaving the panel.
const KEYWORD_SHAPE_HINTS: Record<string, string> = {
  category:
    'Research within a category, narrow or broad (e.g. "enduro mtb bike helmet"). Audience compares options in the category.',
  specific_product:
    'Names a specific product or model (e.g. "fox racing proframe rs"). Audience researches the category, with this product as one candidate.',
  comparison:
    'Compares two or more named alternatives (e.g. "fox vs bell mtb helmets").',
  informational:
    'How/why/what question, not product-focused (e.g. "are MIPS helmets worth it").',
  transactional:
    'Buy/price/discount intent (e.g. "cheap helmets under $100").',
};

// Chip color per shape so the reviewer can sanity-check the classification
// at a glance from across the panel.
const KEYWORD_SHAPE_CHIP: Record<string, string> = {
  category: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  specific_product: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  comparison: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  informational: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
  transactional: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800',
};

const PHASE_ORDER: BuildPhase[] = ['research', 'angle', 'outline', 'write', 'ground', 'seo', 'image', 'final'];

function nextPhase(phase: BuildPhase): BuildPhase | null {
  const i = PHASE_ORDER.indexOf(phase);
  return i >= 0 && i < PHASE_ORDER.length - 1 ? PHASE_ORDER[i + 1] : null;
}

// Phases the backend can actually interrupt. All of them read
// ManagedPage.cancel_requested_at now — the ReAct loops at iteration/phase
// boundaries (should_cancel / _check_cancel) and Synthesize between sources —
// so Stop halts real work rather than just releasing the UI.
//
// Angle and Header image are the exceptions: one structured call each, with no
// loop to break out of, so there's no checkpoint to honor a cancel at. They're
// also the fastest phases, so there's little to interrupt — the image phase
// finishes at "generation queued" and the drawing itself is a separate job.
// A phase that hangs anyway is covered by the stall detector's Clear stuck
// state, which is phase-agnostic.
const STOPPABLE_PHASES: BuildPhase[] = ['research', 'outline', 'write', 'ground', 'seo'];

// The run button's weight follows the phase state, so the eye lands on the
// thing that actually wants doing without reading any text:
//   pending — the step you haven't done. Primary.
//   ready   — done. Demoted; re-running is optional and costs tokens.
//   stale   — an upstream phase moved after this one ran, so this artifact
//             AND everything downstream of it no longer reflect their
//             inputs. Loudest treatment on the screen: it's the one state
//             where doing nothing silently ships something wrong.
function runButtonTheme(state: PhaseStatus['state']): string {
  switch (state) {
    case 'stale':
      return 'text-white bg-amber-500 hover:bg-amber-600 ring-2 ring-amber-300 dark:ring-amber-700/60';
    case 'ready':
      return 'text-purple-700 dark:text-purple-300 bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30';
    case 'pending':
    default:
      return 'text-white bg-purple-600 hover:bg-purple-700';
  }
}

// Research APPENDS turns rather than replacing them (Reset is the wipe), so
// it reads "extract more" rather than "re-run".
//
// The wording matters because there are two research buttons doing genuinely
// different things, and the old labels ("Gather research" / "Re-crawl and
// summarize") didn't distinguish them:
//   * this one   → synthesize job; re-reads pages ALREADY downloaded. Cheap,
//                  no network, page-scoped.
//   * the other  → crawl job; goes back out to the search engine and
//                  re-fetches everything. Slow, and KEYWORD-scoped, so it
//                  hits every managed page on the keyword.
function runButtonLabel(phase: BuildPhase, state: PhaseStatus['state']): string {
  if (phase === 'research') {
    return state === 'pending'
      ? 'Extract facts from crawled pages'
      : 'Extract more facts';
  }
  return state === 'pending'
    ? `Run ${PHASE_LABEL[phase]}`
    : `Re-run ${PHASE_LABEL[phase]}`;
}

// The style the angle committed us to authoring. `recommended_page_type` is
// the legacy key, still present on angles generated before the switch.
function anglePageStyle(angle?: Angle | null): string | undefined {
  if (!angle) return undefined;
  return angle.page_style || angle.recommended_page_type || undefined;
}

const PHASE_LABEL: Record<BuildPhase, string> = {
  research: 'Research',
  angle: 'Angle',
  outline: 'Outline',
  write: 'Write',
  ground: 'Ground',
  seo: 'SEO',
  image: 'Header image',
  final: 'Final page',
};

// What the phase is DOING while it runs — present continuous, so the stepper
// reads as a live status ("Crawling & collecting facts") rather than a noun.
const RUNNING_LABEL: Record<BuildPhase, string> = {
  research: 'Gathering facts',
  angle: 'Choosing angle',
  outline: 'Building outline',
  write: 'Writing document',
  ground: 'Fact-checking',
  seo: 'Optimizing SEO',
  image: 'Making the header image',
  final: 'Working',
};

const PHASE_DESCRIPTION: Record<BuildPhase, string> = {
  research: 'SERP reads + recorded facts that the article will cite.',
  angle: 'Strategic positioning + framing (audience, intent, north star). Drives the outline.',
  outline: 'Title + meta + H2 sections with key points and fact citations.',
  write: 'Full markdown body assembled section by section.',
  ground: 'Compliance + grounding pass - flags unsupported claims and structural violations.',
  seo: 'Auto-corrects on-page SEO (title, meta, keyword placement) on the structured document - each edit eval-gated before it commits.',
  image: "The page's header image, generated from the angle's brief. Feedback nudges the picture (\"moodier\", \"no people\") without rewriting the angle.",
  final: 'The rendered article - title, slug, description, body. Click any field to edit manually.',
};

function safeHost(url?: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function phaseDot(state: PhaseStatus['state']): { bg: string; border: string; label: string } {
  switch (state) {
    case 'ready':
      return { bg: 'bg-emerald-500', border: 'border-emerald-500', label: 'ready' };
    case 'stale':
      return { bg: 'bg-amber-400', border: 'border-amber-400 dark:border-amber-700', label: 'stale' };
    case 'pending':
    default:
      return { bg: 'bg-gray-300', border: 'border-gray-300 dark:border-gray-600', label: 'not yet' };
  }
}

// "2m 14s" / "45s" / "1h 3m" — a duration you can read at a glance and judge
// against, which is what turns a spinner into information.
function formatSeconds(total: number): string {
  const secs = Math.max(0, Math.round(total));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function formatElapsed(sinceIso: string): string | null {
  const started = Date.parse(sinceIso);
  if (Number.isNaN(started)) return null;
  return formatSeconds((Date.now() - started) / 1000);
}

// Ticks once a second so an elapsed-time label stays live. Returns null when
// there's nothing running, so the interval isn't kept alive for no reason.
function useElapsed(sinceIso?: string | null): string | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sinceIso) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [sinceIso]);
  return sinceIso ? formatElapsed(sinceIso) : null;
}

function PhaseStepper({
  phases,
  active,
  onSelect,
  runningPhase,
  runningSince,
  runtimes,
}: {
  phases: PhaseStatus[];
  active: BuildPhase;
  onSelect: (p: BuildPhase) => void;
  // Which phase the server says is executing right now, and since when.
  // Durable (ManagedPage.current_phase*), so this survives a refresh and
  // shows the true elapsed time rather than restarting from zero.
  runningPhase?: BuildPhase | null;
  runningSince?: string | null;
  // How long each phase took last time — the same clock, kept after the run
  // ends so "Write · 4m 02s" is still answerable tomorrow.
  runtimes?: Record<string, { seconds: number; finished_at: string }>;
}) {
  const byPhase = new Map(phases.map((p) => [p.phase, p]));
  const elapsed = useElapsed(runningPhase ? runningSince : null);
  return (
    <ol className="space-y-1">
      {PHASE_ORDER.map((p, i) => {
        const status = byPhase.get(p) ?? { phase: p, state: 'pending' as const };
        const dot = phaseDot(status.state);
        const isActive = p === active;
        const isRunning = p === runningPhase;
        return (
          <li key={p}>
            <button
              onClick={() => onSelect(p)}
              className={`w-full text-left flex items-start gap-3 px-3 py-2 rounded-md transition ${
                isActive ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800' : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
              }`}
            >
              <div className="flex flex-col items-center pt-0.5 flex-shrink-0">
                {/* A running phase gets a spinner in place of its dot — the
                    one row on the stepper that's actually doing something
                    shouldn't look identical to the ones that aren't. */}
                {isRunning ? (
                  <svg
                    className="w-3 h-3 animate-spin text-blue-600 dark:text-blue-400"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                ) : (
                  <div
                    className={`w-3 h-3 rounded-full ${dot.bg} ${
                      isActive ? 'ring-2 ring-blue-300 ring-offset-2' : ''
                    }`}
                  />
                )}
                {i < PHASE_ORDER.length - 1 && (
                  <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mt-1" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className={`text-sm font-medium ${isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                    {PHASE_LABEL[p]}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wide ${
                      isRunning
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {isRunning ? RUNNING_LABEL[p] : dot.label}
                  </span>
                </div>
                {isRunning ? (
                  <div className="text-xs text-blue-600 dark:text-blue-400 tabular-nums">
                    {elapsed ? `running · ${elapsed}` : 'running…'}
                  </div>
                ) : (
                  <>
                    {status.summary && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{status.summary}</div>
                    )}
                    {/* How long it took last time. Useful before pressing
                        Run — a phase that took 6 minutes yesterday will take
                        about that again, and that's worth knowing first. */}
                    {runtimes?.[p] && (
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        took {formatSeconds(runtimes[p].seconds)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function CollapsibleSection({
  title,
  count,
  badge,
  defaultOpen = false,
  headerActions,
  children,
}: {
  title: string;
  count?: number;
  badge?: string;
  defaultOpen?: boolean;
  // Controls that belong to this section specifically — rendered to the
  // right of the title and don't toggle the collapse when clicked.
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800">
      <div className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left flex-1 min-w-0"
        >
          <svg
            className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${
              open ? 'rotate-90' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
          {typeof count === 'number' && (
            <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
              {count}
            </span>
          )}
          {badge && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded">
              {badge}
            </span>
          )}
        </button>
        {headerActions && (
          <div
            className="flex items-center gap-1.5 ml-2 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {headerActions}
          </div>
        )}
      </div>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </section>
  );
}

function InjectionMapTable({ entries }: { entries: InjectionMapEntry[] }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Where this page's data flows when the agent runs. Counts reflect
        what's currently saved.
      </p>
      <div className="border border-gray-200 dark:border-gray-700 rounded overflow-hidden text-sm">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-2 py-1.5 text-left">Source</th>
              <th className="px-2 py-1.5 text-left">Where it's injected</th>
              <th className="px-2 py-1.5 text-right">Now</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {entries.map((e, i) => (
              <tr key={i} className="align-top">
                <td className="px-2 py-2">
                  <div className="text-gray-900 dark:text-gray-100 font-medium">{e.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{e.description}</div>
                </td>
                <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-xs">{e.where}</td>
                <td className="px-2 py-2 text-right text-xs font-mono text-gray-600 dark:text-gray-300">
                  {typeof e.count === 'number' ? e.count : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PageTypeDistribution({
  counts,
}: {
  counts: Record<string, number>;
}) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-gray-500 dark:text-gray-400 font-medium">Source pages:</span>
      {entries.map(([type, count]) => (
        <span
          key={type}
          className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800"
        >
          {count} {type}
        </span>
      ))}
    </div>
  );
}

function ResearchPointRow({
  point,
  pageId,
  onChanged,
  onRegisterSave,
}: {
  point: ResearchPoint;
  pageId: number;
  onChanged: () => void;
  // Report an open inline edit upward (null when closed) so Next can commit
  // it before the Research panel unmounts. Takes the point id because one
  // parent aggregates every row.
  onRegisterSave?: (
    pointId: number,
    save: (() => Promise<void>) | null
  ) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(point.key_point);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!draft.trim() || draft.trim() === point.key_point) {
      setEditing(false);
      setDraft(point.key_point);
      return;
    }
    setBusy(true);
    try {
      await api.updateResearchPoint(pageId, point.id, { key_point: draft.trim() });
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  // `save` already lets errors propagate, which is what the flush needs —
  // a failed commit must reach handleNext so it keeps you on this phase.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    onRegisterSave?.(point.id, editing ? () => saveRef.current() : null);
    return () => onRegisterSave?.(point.id, null);
  }, [editing, point.id, onRegisterSave]);

  const togglePin = async () => {
    setBusy(true);
    try {
      await api.updateResearchPoint(pageId, point.id, { is_pinned: !point.is_pinned });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteResearchPoint(pageId, point.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-800 text-sm flex flex-col gap-1">
      <div className="flex items-start gap-2">
        {editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm font-normal"
            disabled={busy}
          />
        ) : (
          <div className="flex-1 text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
            {point.is_pinned && (
              <span className="mr-1 text-amber-600 dark:text-amber-400" title="Pinned">
                ★
              </span>
            )}
            {point.key_point}
            {point.user_edited_at && (
              <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500 italic">
                (edited)
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <button
                onClick={save}
                disabled={busy}
                className="text-xs px-2 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setDraft(point.key_point);
                }}
                disabled={busy}
                className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={togglePin}
                disabled={busy}
                title={point.is_pinned ? 'Unpin' : 'Pin'}
                className={`text-xs px-1.5 py-0.5 rounded border ${
                  point.is_pinned
                    ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                ★
              </button>
              <button
                onClick={() => setEditing(true)}
                disabled={busy}
                className="text-xs px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Edit
              </button>
              <button
                onClick={remove}
                disabled={busy}
                className="text-xs px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function ManualPointAdder({
  pageId,
  onAdded,
}: {
  pageId: number;
  onAdded: () => void;
}) {
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.addResearchPoint(pageId, text.trim(), url.trim() || undefined);
      setText('');
      setUrl('');
      onAdded();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-800 flex flex-col gap-1.5">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a research point manually (expert knowledge, missing facts, etc.)"
        rows={2}
        className="border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-sm"
        disabled={busy}
      />
      <div className="flex items-center gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Source URL (optional)"
          className="flex-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs"
          disabled={busy}
        />
        <button
          onClick={submit}
          disabled={busy || !text.trim()}
          className="text-xs px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Add point'}
        </button>
      </div>
    </div>
  );
}

function SerpResearchPointsView({
  pageId,
  points,
  pendingSources,
  pageTypeCounts,
  running,
  onPointsChanged,
  serpFoundNothing,
  crawlReadiness,
  onRecrawl,
  recrawling,
  onRegisterSave,
}: {
  pageId: number;
  points: ResearchPoint[];
  pendingSources?: PendingSource[];
  pageTypeCounts: Record<string, number>;
  running: boolean;
  onPointsChanged: () => void;
  serpFoundNothing?: boolean;
  crawlReadiness?: CrawlReadiness | null;
  onRecrawl?: () => void;
  recrawling?: boolean;
  // One flush for the whole panel, covering however many rows are mid-edit.
  onRegisterSave?: (save: (() => Promise<void>) | null) => void;
}) {
  // Rows register their open edits here. We hand the parent a single flush
  // that commits all of them — registered once for the panel's lifetime
  // rather than toggling as rows open and close, so the row registrations
  // don't have to re-render this list.
  const rowSaves = useRef<Map<number, () => Promise<void>>>(new Map());
  const registerRowSave = useCallback(
    (pointId: number, save: (() => Promise<void>) | null) => {
      if (save) rowSaves.current.set(pointId, save);
      else rowSaves.current.delete(pointId);
    },
    []
  );
  useEffect(() => {
    onRegisterSave?.(async () => {
      // Sequential: these are tiny PATCHes and a failure should stop the
      // rest rather than leaving a half-committed set behind.
      for (const save of Array.from(rowSaves.current.values())) {
        await save();
      }
    });
    return () => onRegisterSave?.(null);
  }, [onRegisterSave]);
  // Group by source URL so the UI mirrors "what each ranking page
  // contributed." Manual adds (no serp_entry_id) bucket under a synthetic
  // "Manually added" group at the bottom.
  type Group = {
    key: string;
    title: string;
    url: string;
    rank: number | null;
    page_type: string | null;
    points: ResearchPoint[];
  };
  const groups: Group[] = useMemo(() => {
    const byKey = new Map<string, Group>();
    for (const p of points) {
      const key = p.is_manual ? '__manual__' : p.source_url || `id:${p.id}`;
      let g = byKey.get(key);
      if (!g) {
        g = {
          key,
          title: p.is_manual
            ? 'Manually added'
            : p.source_title || safeHost(p.source_url) || p.source_url,
          url: p.source_url,
          rank: p.serp_rank,
          page_type: p.source_page_type,
          points: [],
        };
        byKey.set(key, g);
      }
      g.points.push(p);
    }
    return Array.from(byKey.values()).sort((a, b) => {
      // Manual group always last; otherwise sort by SERP rank ascending.
      if (a.key === '__manual__') return 1;
      if (b.key === '__manual__') return -1;
      return (a.rank ?? 999) - (b.rank ?? 999);
    });
  }, [points]);

  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);

  const deleteGroup = async (g: Group) => {
    if (
      !window.confirm(
        `Delete all ${g.points.length} fact${
          g.points.length === 1 ? '' : 's'
        } from ${g.title}?`,
      )
    )
      return;
    setDeletingGroup(g.key);
    try {
      await api.bulkDeleteResearchPoints(
        pageId,
        g.points.map((p) => p.id),
      );
      onPointsChanged();
    } finally {
      setDeletingGroup(null);
    }
  };

  return (
    <div className="space-y-3">
      <PageTypeDistribution counts={pageTypeCounts} />
      {/* Crawl gate: extraction coverage for the keyword's ranking pages.
          Read-only — the re-crawl it used to carry now lives in the Run
          button's caret menu, next to the cheap local extract it escalates
          from (the caret turns amber when these chips show failures). */}
      {crawlReadiness && crawlReadiness.has_serps && crawlReadiness.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500 dark:text-gray-400">Extraction:</span>
          <span className="px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-100 dark:border-green-800">
            {crawlReadiness.extracted}/{crawlReadiness.total} extracted
          </span>
          {crawlReadiness.extracting > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              {crawlReadiness.extracting} extracting…
            </span>
          )}
          {crawlReadiness.pending > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
              {crawlReadiness.pending} pending
            </span>
          )}
          {crawlReadiness.failed > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
              {crawlReadiness.failed} failed
            </span>
          )}
          {recrawling && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
              <RefreshGlyph spinning />
              Re-fetching SERP…
            </span>
          )}
        </div>
      )}
      {running && (
        <div className="text-xs text-indigo-600 dark:text-indigo-400 italic">
          Synthesizing - rows will stream in as each source page is processed…
        </div>
      )}
      {points.length === 0 &&
        (!pendingSources || pendingSources.length === 0) &&
        !running &&
        !serpFoundNothing && (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            No research points yet. Run Synthesize to pull the key points out of
            the search results for this page's keyword.
          </div>
        )}
      {/* Shown whenever the last Synthesize added no new facts — whether the
          panel is empty (nothing extractable) or already has facts (a re-run
          dedup'd to zero). Either way the fix is fresh SERPs, not another
          Synthesize over the same cached pages. */}
      {serpFoundNothing && !running && (
        <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
            <div className="min-w-0">
              <div className="font-medium text-amber-900 dark:text-amber-200">
                {points.length === 0
                  ? 'No research facts came back.'
                  : 'No new facts came back.'}
              </div>
              <div className="text-amber-800 dark:text-amber-300 mt-0.5">
                Extracting again won't help - there's nothing more to pull from
                this keyword's current SERPs. Re-fetch them to crawl fresh
                pages; extraction re-runs automatically when the crawl lands.
              </div>
              {/* Same action the Run button's caret offers, surfaced inline
                  because this is the moment you need it. One handler, so the
                  two can't drift apart. */}
              {onRecrawl && (
                <button
                  type="button"
                  onClick={onRecrawl}
                  disabled={recrawling}
                  className="mt-2 px-3 py-1 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  {recrawling ? 'Re-fetching SERP…' : 'Re-fetch SERP & re-extract'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {groups.length > 0 && (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.key} className="border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 p-2">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  {g.rank !== null && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono shrink-0">
                      #{g.rank}
                    </span>
                  )}
                  {g.page_type && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 shrink-0">
                      {g.page_type}
                    </span>
                  )}
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {g.title}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {g.url && (
                    <a
                      href={g.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      {safeHost(g.url) || 'open'}
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteGroup(g)}
                    disabled={deletingGroup === g.key}
                    title="Delete all facts from this source"
                    className="text-xs px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-50"
                  >
                    {deletingGroup === g.key ? 'Deleting…' : 'Delete all'}
                  </button>
                </div>
              </div>
              <ol className="space-y-1.5">
                {g.points.map((p) => (
                  <ResearchPointRow
                    key={p.id}
                    point={p}
                    pageId={pageId}
                    onChanged={onPointsChanged}
                    onRegisterSave={registerRowSave}
                  />
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
      {/* Point-less sources: SERP competitors whose facts haven't extracted yet.
          They already feed the angle stage — show them so the tab isn't empty
          pre-extraction (title + snippet + rank + extraction status). */}
      {pendingSources && pendingSources.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Competitors awaiting extraction ({pendingSources.length}) - these
            already feed the angle stage
          </div>
          {pendingSources.map((s) => (
            <div
              key={s.id}
              className="border border-dashed border-gray-300 dark:border-gray-600 rounded bg-gray-50/60 dark:bg-gray-800 p-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                {s.serp_rank !== null && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono shrink-0">
                    #{s.serp_rank}
                  </span>
                )}
                {s.source_page_type && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 shrink-0">
                    {s.source_page_type}
                  </span>
                )}
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {s.source_title || safeHost(s.source_url) || '(untitled)'}
                </div>
                <span
                  className={`ml-auto shrink-0 text-[10px] px-1.5 py-0.5 rounded ${
                    s.extraction_state === 'extracting'
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300'
                      : s.extraction_state === 'failed'
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {s.extraction_state === 'extracting'
                    ? 'extracting…'
                    : s.extraction_state === 'failed'
                      ? 'extraction failed'
                      : 'no facts yet'}
                </span>
              </div>
              {s.detail && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{s.detail}</div>
              )}
            </div>
          ))}
        </div>
      )}
      <ManualPointAdder pageId={pageId} onAdded={onPointsChanged} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Angle (Stage 2): strategic-positioning view
// ---------------------------------------------------------------------------

const STRATEGIC_MOVE_LABEL: Record<string, string> = {
  match_and_beat: 'Match and beat',
  aggregate_up: 'Aggregate up',
  educate_down: 'Educate down',
  synthesize_across: 'Synthesize across',
  differentiate: 'Differentiate',
};

const STRATEGIC_MOVE_CHIP: Record<string, string> = {
  match_and_beat: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  aggregate_up: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  educate_down: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  synthesize_across: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  differentiate: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800',
};

// Cosmetic rotating status while the angle is generating. The
// underlying call is one shot of complete_structured (no streaming
// possible alongside response_format), so there's nothing real to
// stream — these stages are timed copy that maps roughly to what the
// model is doing, picked to feel honest without claiming live
// progress. Stays on the last stage once it cycles through.
const ANGLE_STATUS_STAGES = [
  'Reading research points…',
  'Reading SERP landscape…',
  'Picking strategic move…',
  'Drafting framing…',
  'Structuring outline sections…',
  'Finalizing angle…',
];
const ANGLE_STATUS_INTERVAL_MS = 4000;

function useRotatingStatus(running: boolean, stages: string[]): string | null {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!running) {
      setIdx(0);
      return;
    }
    const id = setInterval(() => {
      setIdx((cur) => Math.min(cur + 1, stages.length - 1));
    }, ANGLE_STATUS_INTERVAL_MS);
    return () => clearInterval(id);
  }, [running, stages.length]);
  if (!running) return null;
  return stages[idx];
}

// The `image` phase's panel: what the selected angle asked for, what we
// actually have, and whether the two still agree.
//
// No Run button of its own — the phase header owns that, same as every other
// phase, so the concierge's run_phase tool and the Next flow reach it too. The
// phase itself completes at "generation queued"; the picture lands later, and
// the generate_image job announces that with an `image_generated` event —
// which is what this listens for, like ImagesPanel does, rather than standing
// up a second freshness mechanism on a screen that already has one.
function HeroImagePanel({ pageId }: { pageId: number }) {
  const [hero, setHero] = useState<HeroImageState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { subscribe } = useWebSocket();

  const load = useCallback(async () => {
    try {
      setHero(await api.getHeroImage(pageId));
    } catch (e) {
      console.error('Failed to load hero image', e);
    }
  }, [pageId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.event_type === 'image_generated' && event.page_id === pageId) {
        load();
      }
    });
    return unsub;
  }, [subscribe, load, pageId]);

  const clearPin = async () => {
    setBusy(true);
    try {
      setHero(await api.setHeroImage(pageId, null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear');
    } finally {
      setBusy(false);
    }
  };

  const generateCandidate = async (prompt: string) => {
    setBusy(true);
    setError(null);
    try {
      setHero(await api.generateHeroCandidate(pageId, prompt));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start generation');
    } finally {
      setBusy(false);
    }
  };

  if (!hero) return <div className="text-sm text-gray-400">Loading…</div>;

  if (!hero.candidates.length) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        No header-image brief yet - run Angle first. (Angles generated before
        header images existed don't carry one, so re-running Angle is what
        produces them.)
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hero.image_url && (
        <div>
          <img
            src={hero.image_url}
            alt={hero.alt_text || 'Page header image'}
            className="w-full max-w-md rounded border border-gray-200 dark:border-gray-700"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
            <span>{hero.source === 'user' ? 'Pinned by you' : 'Chosen by the angle'}</span>
            {/* Provenance, not a warning: the phase is done. Deliberately not
                amber — a header image is one picture, and nagging at a page
                that already has one is asking for money to silence a badge. */}
            {hero.from_earlier_angle && (
              <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                from an earlier angle
              </span>
            )}
          </div>
        </div>
      )}

      {hero.pending && (
        <div className="flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-300">
          <span className="inline-block h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
          Generating…
        </div>
      )}

      {hero.generation_error && (
        <p className="text-xs text-red-600 dark:text-red-400">{hero.generation_error}</p>
      )}

      {/* Every prompt this page could use. Running the phase always takes the
          selected angle's; these buttons reach past it for one of the others. */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          Prompts ({hero.candidates.length})
        </div>
        <div className="mt-2 space-y-2">
          {hero.candidates.map((c, i) => (
            <div
              key={i}
              className={`rounded border p-2.5 ${
                c.is_current_hero
                  ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                {c.is_current_hero && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                    In use
                  </span>
                )}
                {c.is_selected_angle && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                    Selected angle
                  </span>
                )}
                {c.strategic_move && (
                  <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {c.strategic_move}
                  </span>
                )}
                {c.origin === 'generated' && !c.is_selected_angle && (
                  <span
                    className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                    title="From an angle that has since been replaced - kept because its image already exists"
                  >
                    earlier angle
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{c.prompt}</p>
              <div className="mt-2 flex items-center gap-2">
                {c.image_url ? (
                  <img
                    src={c.image_url}
                    alt=""
                    className="h-12 w-20 object-cover rounded border border-gray-200 dark:border-gray-700"
                  />
                ) : c.pending ? (
                  <span className="text-[11px] text-indigo-600 dark:text-indigo-400">
                    generating…
                  </span>
                ) : null}
                {!c.is_current_hero && !c.pending && (
                  <button
                    onClick={() => generateCandidate(c.prompt)}
                    disabled={busy}
                    title={
                      c.image_url
                        ? 'Use this image as the header'
                        : 'Generate a header image from this prompt'
                    }
                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-50"
                  >
                    {c.image_url ? 'Use this' : 'Generate'}
                  </button>
                )}
                {c.generation_error && (
                  <span className="text-[11px] text-red-600 dark:text-red-400">
                    {c.generation_error}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {hero.source === 'user' && (
        <div>
          <button
            onClick={clearPin}
            disabled={busy}
            title="Hand the slot back to the angle so running this phase can choose"
            className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Unpin
          </button>
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Running this phase leaves a pinned image alone.
          </p>
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

function AnglePanel({
  pageId,
  angle,
  framing,
  running,
  onSaved,
  onFramingSaved,
  onRegisterFramingSave,
  optionCount,
  effectivePageStyle,
  pageStyleSource,
}: {
  pageId: number;
  angle?: Angle | null;
  // Editable framing artifact (page.framing) — hydrated from
  // angle.framing on angle commit but separately editable here so
  // manual tweaks don't require re-running the LLM.
  framing?: FramingArtifact | null;
  running: boolean;
  // No generate/re-run button in here — the phase header owns that action
  // (it called the same handleRunPhase('angle') this panel used to).
  onSaved: () => void;
  onFramingSaved: (next: FramingArtifact) => void;
  // Passed straight through to FramingPanel — see its onRegisterSave.
  onRegisterFramingSave?: (save: (() => Promise<void>) | null) => void;
  // How many angle options exist (>1 → offer the picker).
  optionCount?: number;
  // ManagedPage.page_style + page_style_source — the style the page will
  // actually be authored as, and who chose it. Separate from the angle's own
  // page_style, which records only what this phase picked.
  effectivePageStyle?: string | null;
  pageStyleSource?: string | null;
}) {
  // A user pin that disagrees with the angle's choice. Worth calling out:
  // otherwise the chip silently shows one style while the writer targets
  // another.
  const stylePinnedOverAngle =
    pageStyleSource === 'user' &&
    !!effectivePageStyle &&
    !!anglePageStyle(angle) &&
    anglePageStyle(angle) !== effectivePageStyle;
  const showInternals = useShowInternals();
  const [rawEditing, setRawEditing] = useState(false);
  const [rawDraft, setRawDraft] = useState('');
  const [rawError, setRawError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const rotatingStatus = useRotatingStatus(running, ANGLE_STATUS_STAGES);

  // Angle-option picker state (loaded lazily when the user expands it).
  const [showOptions, setShowOptions] = useState(false);
  const [options, setOptions] = useState<AngleOption[] | null>(null);
  const [selectingId, setSelectingId] = useState<number | null>(null);

  const loadOptions = async () => {
    setShowOptions(true);
    try {
      setOptions(await api.listAngleOptions(pageId));
    } catch (e) {
      console.error('Failed to load angle options', e);
    }
  };

  const selectOption = async (id: number) => {
    setSelectingId(id);
    try {
      await api.selectAngleOption(pageId, id);
      // Move the "selected" chip immediately…
      setOptions((prev) =>
        prev
          ? prev.map((o) => ({ ...o, is_selected: o.id === id }))
          : prev,
      );
      // …and refresh build-state: new selected angle + downstream marked stale.
      onSaved();
    } catch (e) {
      console.error('Failed to select angle option', e);
    } finally {
      setSelectingId(null);
    }
  };

  const startRawEdit = () => {
    setRawDraft(JSON.stringify(angle ?? {}, null, 2));
    setRawError(null);
    setRawEditing(true);
  };

  const cancelRawEdit = () => {
    setRawEditing(false);
    setRawDraft('');
    setRawError(null);
  };

  const saveRawEdit = async () => {
    let parsed: Angle;
    try {
      parsed = JSON.parse(rawDraft);
    } catch (e) {
      setRawError(`Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`);
      return;
    }
    setSaving(true);
    try {
      await api.updatePageAngle(pageId, parsed);
      setRawEditing(false);
      onSaved();
    } catch (e) {
      setRawError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!angle) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No angle generated yet. The angle decides the page's strategic
          positioning in this SERP (match-and-beat / aggregate-up / educate-down
          / synthesize-across / differentiate) and drives framing + outline.
          Run Synthesize first so the generator has research points to work with.
        </div>
        {rotatingStatus && (
          <div className="flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded px-3 py-2">
            <span className="inline-block h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
            <span>{rotatingStatus}</span>
          </div>
        )}
      </div>
    );
  }

  const moveChip =
    STRATEGIC_MOVE_CHIP[angle.strategic_move] ||
    'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700';
  const moveLabel =
    STRATEGIC_MOVE_LABEL[angle.strategic_move] || angle.strategic_move;

  return (
    <div className="space-y-4">
      {rotatingStatus && (
        <div className="flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded px-3 py-2">
          <span className="inline-block h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
          <span>{rotatingStatus}</span>
        </div>
      )}
      {/* Strategic move + the page style it commits us to authoring */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`px-2.5 py-1 rounded-full text-sm font-medium border ${moveChip}`}
        >
          {moveLabel}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">→</span>
        {/* The EFFECTIVE style — what the page will actually be written as.
            The angle carries its own page_style, but that's a record of what
            this phase chose; a pin in Settings overrides it. Showing the
            angle's copy here made this chip disagree with reality (and with
            the spec the writer is graded against) with nothing to say so. */}
        {stylePinnedOverAngle ? (
          <>
            {/* Struck-through: what the angle picked, now overridden. Shown
                rather than hidden so the angle's reasoning below still makes
                sense — it argues for a style the page isn't using. */}
            <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 text-xs line-through">
              {anglePageStyle(angle)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">→</span>
            <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-xs font-medium">
              {effectivePageStyle}
            </span>
            <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium">
              your override
            </span>
          </>
        ) : (
          <span className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-medium">
            {effectivePageStyle || anglePageStyle(angle) || 'unset'}
          </span>
        )}
        {angle.model && showInternals && (
          <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 italic">
            {angle.model}
          </span>
        )}
      </div>

      {/* Spell out the override in full. The chips say WHAT; this says what
          it means — the reasoning below argues for the angle's style, and
          without this the two read as a contradiction rather than a choice
          the user made. */}
      {stylePinnedOverAngle && (
        <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          The Angle phase chose <strong>{anglePageStyle(angle)}</strong> from
          the SERP landscape, but you&rsquo;ve pinned{' '}
          <strong>{effectivePageStyle}</strong> in Settings - so this page is
          written as {effectivePageStyle}, against {effectivePageStyle} word
          targets and section bounds. Re-running the angle won&rsquo;t change
          it. Set the override back to Unset to hand the choice back.
        </div>
      )}

      {/* Angle-option picker — only when more than one option was generated. */}
      {(optionCount ?? 0) > 1 && (
        <div className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3">
          {!showOptions ? (
            <button
              type="button"
              onClick={loadOptions}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Choose a different angle ({optionCount} options)
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Angle options
              </div>
              {options === null ? (
                <div className="text-xs text-gray-400 dark:text-gray-500 italic">Loading…</div>
              ) : (
                options.map((opt) => (
                  <div
                    key={opt.id}
                    className={`flex items-start gap-2 rounded border p-2 ${
                      opt.is_selected
                        ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 text-xs font-medium">
                          {STRATEGIC_MOVE_LABEL[opt.angle.strategic_move] ||
                            opt.angle.strategic_move}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          → {anglePageStyle(opt.angle) || 'unset'}
                        </span>
                        {opt.is_selected && (
                          <span className="text-[10px] font-medium text-indigo-700 dark:text-indigo-300">
                            selected
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                        {(opt.angle.reasoning || '').slice(0, 160)}
                        {(opt.angle.reasoning || '').length > 160 ? '…' : ''}
                      </div>
                    </div>
                    {!opt.is_selected && (
                      <button
                        type="button"
                        onClick={() => selectOption(opt.id)}
                        disabled={selectingId !== null}
                        className="shrink-0 text-xs px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
                      >
                        {selectingId === opt.id ? 'Selecting…' : 'Select'}
                      </button>
                    )}
                  </div>
                ))
              )}
              <div className="text-[11px] text-gray-400 dark:text-gray-500">
                Selecting a different angle re-hydrates framing and marks
                outline/write stale.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reasoning */}
      <CollapsibleSection title="Reasoning" defaultOpen>
        <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
          {angle.reasoning}
        </div>
      </CollapsibleSection>

      {/* Framing — editable in place. Angle commit hydrates these
          fields onto page.framing; the user can tweak without re-running
          the angle LLM. The standalone Framing tab was removed since
          this is the only place the data lives. */}
      <CollapsibleSection title="Framing (editable)" defaultOpen>
        <FramingPanel
          framing={framing}
          pageId={pageId}
          onSaved={onFramingSaved}
          onRegisterSave={onRegisterFramingSave}
        />
      </CollapsibleSection>

      {/* Grouped key points — outline preview */}
      <CollapsibleSection
        title="Grouped key points (outline skeleton)"
        count={angle.grouped_key_points.length}
        defaultOpen
      >
        <div className="space-y-2">
          {angle.grouped_key_points.map((s, idx) => (
            <div
              key={idx}
              className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 p-2"
            >
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {s.h2}
              </div>
              <ul className="mt-1 space-y-1 pl-4 list-disc text-sm text-gray-700 dark:text-gray-300">
                {s.points.map((p, i) => (
                  <li key={i}>
                    {p.key_point}
                    {p.source_url && (
                      <a
                        href={p.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        ({safeHost(p.source_url)})
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Differentiators */}
      {angle.differentiators.length > 0 && (
        <CollapsibleSection
          title="Differentiators"
          count={angle.differentiators.length}
        >
          <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {angle.differentiators.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Actions — re-run is on the phase header's "Re-run angle" button,
          dropping the duplicate here to match how other phase panels work. */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        {!rawEditing ? (
          <Internals>
            <button
              onClick={startRawEdit}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Edit JSON
            </button>
          </Internals>
        ) : null}
        {angle.generated_at && (
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
            generated {new Date(angle.generated_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* JSON escape hatch */}
      {rawEditing && (
        <div className="space-y-2 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 rounded p-3">
          <div className="text-xs text-amber-800 dark:text-amber-300 font-medium">
            Editing raw angle JSON. Saving re-hydrates framing + research_evidence.
          </div>
          <textarea
            value={rawDraft}
            onChange={(e) => setRawDraft(e.target.value)}
            rows={20}
            className="w-full font-mono text-xs border border-gray-300 dark:border-gray-600 rounded p-2"
            disabled={saving}
          />
          {rawError && (
            <div className="text-xs text-rose-700 dark:text-rose-300">{rawError}</div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={saveRawEdit}
              disabled={saving}
              className="text-sm px-3 py-1.5 rounded bg-indigo-600 text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={cancelRawEdit}
              disabled={saving}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResearchPanel({
  pageId,
  researchPoints,
  pendingSources,
  pageTypeCounts,
  evidence,
  injectionMap,
  running,
  onRerun,
  onPointsChanged,
  serpFoundNothing,
  crawlReadiness,
  onRecrawl,
  recrawling,
  onRegisterSave,
}: {
  pageId: number;
  researchPoints: ResearchPoint[];
  pendingSources?: PendingSource[];
  pageTypeCounts: Record<string, number>;
  // Legacy LLM-recorded evidence list — rendered as a collapsible
  // "legacy citations" block below the unified Synthesize view for
  // pages built before Stage 1. Stage 3 cleanup will rip this out.
  evidence?: ResearchEvidenceEntry[] | null;
  injectionMap?: InjectionMapEntry[] | null;
  running: boolean;
  onRerun: () => void;
  onPointsChanged: () => void;
  // Last Synthesize finished against cached SERPs but produced no facts.
  serpFoundNothing?: boolean;
  crawlReadiness?: CrawlReadiness | null;
  onRecrawl?: () => void;
  recrawling?: boolean;
  // Passed through to the points view, which aggregates its rows' open edits.
  onRegisterSave?: (save: (() => Promise<void>) | null) => void;
}) {
  const hasEvidence = !!(evidence && evidence.length);
  const hasPoints = researchPoints.length > 0;

  const synthesizeSection = (
    <CollapsibleSection
      title="Research points"
      count={researchPoints.length}
      badge="synthesize"
      defaultOpen
    >
      <SerpResearchPointsView
        pageId={pageId}
        points={researchPoints}
        pendingSources={pendingSources}
        pageTypeCounts={pageTypeCounts}
        running={running}
        onPointsChanged={onPointsChanged}
        serpFoundNothing={serpFoundNothing}
        crawlReadiness={crawlReadiness}
        onRecrawl={onRecrawl}
        recrawling={recrawling}
        onRegisterSave={onRegisterSave}
      />
    </CollapsibleSection>
  );

  if (!hasEvidence && !hasPoints) {
    return (
      <div className="space-y-3">
        {synthesizeSection}
        {injectionMap && injectionMap.length > 0 && (
          <Internals>
            <CollapsibleSection title="Where data flows" badge="injection map">
              <InjectionMapTable entries={injectionMap} />
            </CollapsibleSection>
          </Internals>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* SERP-derived research points (Synthesize phase) — the primary
          research surface; replaces the LLM-driven fact-gathering loop. */}
      {synthesizeSection}

      {/* Legacy: LLM-recorded citation facts. Still shown for pages built
          before the Synthesize cutover. Stage 2 will remove this. */}
      {hasEvidence && (
        <CollapsibleSection
          title="Recorded research facts (citations)"
          count={evidence!.length}
          defaultOpen
        >
          <ol className="space-y-2">
            {[...evidence!]
              .sort((a, b) => (a.marker_number || 0) - (b.marker_number || 0))
              .map((row) => {
                const sourceLabel = row.source_url ? (
                  <a
                    href={row.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline truncate"
                    title={row.source_url}
                  >
                    {safeHost(row.source_url)}
                  </a>
                ) : row.source_description ? (
                  <span
                    className="text-xs text-gray-600 dark:text-gray-300 italic truncate"
                    title={row.source_description}
                  >
                    {row.source_description}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500 italic">unattributed</span>
                );
                return (
                  <li
                    key={row.marker_number}
                    className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-800 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400 font-mono font-medium">
                        [{row.marker_number}]
                      </span>
                      {row.method && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            row.method === 'crawl'
                              ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                              : row.method === 'search'
                                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {row.method}
                        </span>
                      )}
                      {sourceLabel}
                    </div>
                    <div className="mt-1 text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {row.fact}
                    </div>
                    {row.source_url && row.source_description && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                        {row.source_description}
                      </div>
                    )}
                  </li>
                );
              })}
          </ol>
        </CollapsibleSection>
      )}

      {injectionMap && injectionMap.length > 0 && (
        <Internals>
          <CollapsibleSection title="Where data flows" badge="injection map">
            <InjectionMapTable entries={injectionMap} />
          </CollapsibleSection>
        </Internals>
      )}
    </div>
  );
}

function FramingPanel({
  framing,
  pageId,
  onSaved,
  onRegisterSave,
}: {
  framing?: FramingArtifact | null;
  pageId: number;
  onSaved: (next: FramingArtifact) => void;
  // Hand the parent a flush for an in-progress edit (null when not editing)
  // so Next persists the framing before this panel unmounts.
  onRegisterSave?: (save: (() => Promise<void>) | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FramingArtifact>({
    keyword_shape: framing?.keyword_shape ?? '',
    subject: framing?.subject ?? '',
    entity: framing?.entity ?? '',
    audience: framing?.audience ?? '',
    intent: framing?.intent ?? '',
    north_star: framing?.north_star ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset draft whenever the upstream framing changes (e.g. a re-run completed).
  useEffect(() => {
    if (!editing) {
      setDraft({
        keyword_shape: framing?.keyword_shape ?? '',
        subject: framing?.subject ?? '',
        entity: framing?.entity ?? '',
        audience: framing?.audience ?? '',
        intent: framing?.intent ?? '',
        north_star: framing?.north_star ?? '',
      });
    }
  }, [framing, editing]);

  // Returns null on success or the error message on failure — see the
  // matching note in OutlinePanel.handleSave.
  const handleSave = async (): Promise<string | null> => {
    setSaving(true);
    setError(null);
    try {
      const next = await api.updateFraming(pageId, {
        keyword_shape: draft.keyword_shape ?? '',
        subject: draft.subject ?? '',
        entity: draft.entity ?? '',
        audience: draft.audience ?? '',
        intent: draft.intent ?? '',
        north_star: draft.north_star ?? '',
      });
      onSaved(next);
      setEditing(false);
      return null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      return msg;
    } finally {
      setSaving(false);
    }
  };

  // Only an open editor can hold unsaved text — the read view has nothing
  // to flush.
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;
  useEffect(() => {
    onRegisterSave?.(
      editing
        ? async () => {
            const err = await saveRef.current();
            if (err) throw new Error(err);
          }
        : null
    );
    return () => onRegisterSave?.(null);
  }, [editing, onRegisterSave]);

  if (!editing) {
    if (!framing || !framing.north_star) {
      return (
        <div className="space-y-2">
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            Framing not committed yet. Run the framing phase, or fill it in directly.
          </div>
          <button
            onClick={() => setEditing(true)}
            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Write framing manually
          </button>
        </div>
      );
    }
    const Field = ({ label, value }: { label: string; value?: string | null }) => (
      <div className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-800">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          {label}
        </div>
        <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
          {value || '(not set)'}
        </div>
      </div>
    );
    const shape = (framing.keyword_shape || '').trim();
    const chipClass =
      KEYWORD_SHAPE_CHIP[shape] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    return (
      <div className="space-y-2">
        <div className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-800">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Keyword shape
          </div>
          {shape ? (
            <span
              className={`inline-block text-xs font-medium px-2 py-0.5 border rounded ${chipClass}`}
              title={KEYWORD_SHAPE_HINTS[shape] || ''}
            >
              {shape}
            </span>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500 italic">(not classified)</span>
          )}
        </div>
        <Field label="Subject (category / topic the page is about)" value={framing.subject} />
        {framing.entity && framing.entity.trim() ? (
          <Field label="Featured entity (specific named brand/product)" value={framing.entity} />
        ) : null}
        <Field label="Audience" value={framing.audience} />
        <Field label="Intent" value={framing.intent} />
        <Field
          label="The one thing they walk away with"
          value={framing.north_star}
        />
        <button
          onClick={() => setEditing(true)}
          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Edit
        </button>
      </div>
    );
  }

  const Input = ({
    label,
    value,
    onChange,
    multi,
  }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    multi?: boolean;
  }) => (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </span>
      {multi ? (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      )}
    </label>
  );

  const draftShape = (draft.keyword_shape ?? '').toString();
  return (
    <div className="space-y-2">
      <label className="block">
        <span className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          Keyword shape
        </span>
        <select
          value={draftShape}
          onChange={(e) =>
            setDraft((d) => ({ ...d, keyword_shape: e.target.value }))
          }
          className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-800"
        >
          <option value="">(not classified)</option>
          {KEYWORD_SHAPES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {draftShape && KEYWORD_SHAPE_HINTS[draftShape] && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            {KEYWORD_SHAPE_HINTS[draftShape]}
          </p>
        )}
      </label>
      <Input
        label="Subject (category / topic the page is about)"
        value={draft.subject ?? ''}
        onChange={(v) => setDraft((d) => ({ ...d, subject: v }))}
      />
      <Input
        label="Featured entity (leave blank if no specific brand/product)"
        value={draft.entity ?? ''}
        onChange={(v) => setDraft((d) => ({ ...d, entity: v }))}
      />
      <Input
        label="Audience"
        value={draft.audience ?? ''}
        onChange={(v) => setDraft((d) => ({ ...d, audience: v }))}
      />
      <Input
        label="Intent"
        value={draft.intent ?? ''}
        onChange={(v) => setDraft((d) => ({ ...d, intent: v }))}
      />
      <Input
        label="The one thing they walk away with"
        value={draft.north_star ?? ''}
        onChange={(v) => setDraft((d) => ({ ...d, north_star: v }))}
        multi
      />
      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save framing'}
        </button>
        <button
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
        >
          Cancel
        </button>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
        Editing framing here does exactly what the agent does when it sets the
        framing itself. The new values are picked up by the outline, write and
        ground phases on their next run.
      </p>
    </div>
  );
}

interface OutlineFrontmatter {
  title?: string;
  description?: string;
  slug?: string;
  [k: string]: unknown;
}

interface OutlineSection {
  heading?: string;
  target_words?: number;
  key_points?: string[];
  facts?: string[];
  angle?: string;
}

interface ParsedOutline {
  frontmatter: OutlineFrontmatter;
  sections: OutlineSection[];
}

function parseOutline(outline?: string | null): ParsedOutline | null {
  if (!outline) return null;
  try {
    const data = JSON.parse(outline);
    return {
      frontmatter: (data.frontmatter as OutlineFrontmatter) || {},
      sections: (data.sections as OutlineSection[]) || [],
    };
  } catch {
    return null;
  }
}

function blankSection(): OutlineSection {
  return {
    heading: '',
    target_words: 250,
    key_points: [],
    facts: [],
    angle: '',
  };
}

// NOTE: the "use the angle's structure raw" mode used to live here as a
// checkbox above every branch of this panel. It's a property of the RUN, so it
// moved onto the Run button's caret menu (see runMenuItems), which also shows
// the mode in force as a chip on the button itself.
function OutlinePanel({
  outline,
  pageId,
  onSaved,
  onRegisterSave,
}: {
  outline?: string | null;
  pageId: number;
  onSaved: () => void;
  // Hand the parent a flush for unsaved edits (null when clean) so Next can
  // persist them before this panel unmounts.
  onRegisterSave?: (save: (() => Promise<void>) | null) => void;
}) {
  const parsed = useMemo(() => parseOutline(outline), [outline]);
  const [draft, setDraft] = useState<ParsedOutline | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // `insertIndex` is the slot the dragged item would land in if dropped
  // right now — 0 = before the first card, sections.length = after the
  // last. Computed from cursor Y vs. each card's vertical midpoint, so
  // the visual indicator sits *between* cards instead of highlighting
  // whichever card the cursor happens to be over.
  const [insertIndex, setInsertIndex] = useState<number | null>(null);

  // Reset draft when the upstream outline changes (e.g. after a re-run).
  // Skip while the user has unsaved local edits so a websocket refresh
  // doesn't clobber them.
  useEffect(() => {
    if (!dirty) {
      setDraft(parsed);
    }
  }, [parsed, dirty]);

  // Registered with the parent so Next can flush unsaved edits before this
  // panel unmounts. Both hooks have to run before the early returns below,
  // which is why the ref is filled in further down rather than declared next
  // to the handleSave it calls.
  const saveRef = useRef<(() => Promise<string | null>) | null>(null);
  useEffect(() => {
    onRegisterSave?.(
      dirty
        ? async () => {
            const err = await saveRef.current?.();
            if (err) throw new Error(err);
          }
        : null
    );
    return () => onRegisterSave?.(null);
  }, [dirty, onRegisterSave]);

  // `!draft` matters: "Start a blank outline" below seeds a local draft
  // while the server-side `outline` is still null. Without it this branch
  // re-returned the empty state forever and the button did nothing visible.
  if (!outline && !draft) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No outline yet. The agent produces this in the outline phase, or
          you can build one manually below.
          <button
            type="button"
            onClick={() => {
              setDraft({ frontmatter: {}, sections: [blankSection()] });
              setDirty(true);
            }}
            className="ml-2 text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Start a blank outline
          </button>
        </div>
      </div>
    );
  }
  if (!parsed && !draft) {
    return (
      <div className="space-y-3">
        <div className="text-xs text-amber-700 dark:text-amber-300 mb-2">
          This outline came back malformed. Re-run the outline phase to try again.
        </div>
        <Internals>
          <pre className="text-xs bg-gray-50 dark:bg-gray-800 border rounded p-2 overflow-auto whitespace-pre-wrap">
            {outline}
          </pre>
        </Internals>
      </div>
    );
  }
  const view = draft || parsed!;

  const updateSection = (i: number, patch: Partial<OutlineSection>) => {
    const next = view.sections.slice();
    next[i] = { ...next[i], ...patch };
    setDraft({ ...view, sections: next });
    setDirty(true);
  };

  const updateFrontmatter = (patch: Partial<OutlineFrontmatter>) => {
    setDraft({ ...view, frontmatter: { ...view.frontmatter, ...patch } });
    setDirty(true);
  };

  const addSection = () => {
    setDraft({ ...view, sections: [...view.sections, blankSection()] });
    setDirty(true);
  };

  const deleteSection = (i: number) => {
    if (!window.confirm(`Delete section ${i + 1}?`)) return;
    setDraft({
      ...view,
      sections: view.sections.filter((_, idx) => idx !== i),
    });
    setDirty(true);
  };

  const moveSection = (from: number, to: number) => {
    if (from === to) return;
    const next = view.sections.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDraft({ ...view, sections: next });
    setDirty(true);
  };

  // Returns null on success or the error message on failure. The inline
  // Save button just renders `error`; Next's flush needs the return value so
  // it can keep you on this phase when the save didn't land.
  const handleSave = async (): Promise<string | null> => {
    setSaving(true);
    setError(null);
    try {
      // The PUT endpoint will diff against the current outline and create
      // a new ManagedPageContentVersion when there's a real change, so
      // version history is preserved across manual edits.
      await api.updateManagedPage(pageId, {
        outline: JSON.stringify({
          frontmatter: view.frontmatter,
          sections: view.sections,
        }),
      });
      setDirty(false);
      onSaved();
      return null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      return msg;
    } finally {
      setSaving(false);
    }
  };

  // Point the registered flush at the current closure. A plain assignment,
  // not a hook, so it's fine down here after the early returns.
  saveRef.current = handleSave;

  const handleRevert = () => {
    setDraft(parsed);
    setDirty(false);
    setError(null);
  };

  // Pipe-separated ⇄ array helpers so the user can edit key_points / facts
  // as a single textarea without juggling JSON.
  const joinList = (xs?: string[]) => (xs && xs.length ? xs.join(' | ') : '');
  const splitList = (s: string): string[] =>
    s
      .split('|')
      .map((p) => p.trim())
      .filter(Boolean);

  return (
    <div className="space-y-3">
      {/* Frontmatter */}
      <div className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-800 space-y-2">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Article meta
        </div>
        <input
          type="text"
          placeholder="Title"
          value={view.frontmatter.title || ''}
          onChange={(e) => updateFrontmatter({ title: e.target.value })}
          className="w-full text-base font-semibold text-gray-900 dark:text-gray-100 border-0 border-b border-gray-100 dark:border-gray-700 focus:outline-none focus:border-blue-400 dark:focus:border-blue-700 px-0 py-1"
        />
        <textarea
          rows={2}
          placeholder="Meta description"
          value={view.frontmatter.description || ''}
          onChange={(e) => updateFrontmatter({ description: e.target.value })}
          className="w-full text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <input
          type="text"
          placeholder="url-slug"
          value={view.frontmatter.slug || ''}
          onChange={(e) => updateFrontmatter({ slug: e.target.value })}
          className="w-full text-xs text-gray-700 dark:text-gray-300 font-mono border border-gray-200 dark:border-gray-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Sections */}
      {(() => {
        // Drop indicator is a thin blue bar in the gap between cards.
        // We hide it when the slot would be a no-op (immediately before
        // or after the dragged card) so the user only sees an indicator
        // when the drop will actually move something.
        const isNoop =
          dragIndex !== null &&
          insertIndex !== null &&
          (insertIndex === dragIndex || insertIndex === dragIndex + 1);
        const showAt = (slot: number) =>
          dragIndex !== null && insertIndex === slot && !isNoop;
        const Indicator = ({ slot }: { slot: number }) =>
          showAt(slot) ? (
            <div
              className="h-1 bg-blue-500 rounded"
              aria-hidden
            />
          ) : null;
        // The list-level handlers cover the gaps between cards, so a
        // dragOver in the visible space (not just on a card) still
        // updates insertIndex. Per-card handlers refine the slot based
        // on cursor-vs-midpoint.
        const handleListDragOver = (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        };
        const handleListDrop = (e: React.DragEvent) => {
          e.preventDefault();
          if (dragIndex !== null && insertIndex !== null) {
            // insertIndex is in pre-removal coordinates; once the dragged
            // card is removed, indices after it shift down by one.
            const target =
              insertIndex > dragIndex ? insertIndex - 1 : insertIndex;
            if (target !== dragIndex) {
              moveSection(dragIndex, target);
            }
          }
          setDragIndex(null);
          setInsertIndex(null);
        };
        return (
        <ol
          className="space-y-2"
          onDragOver={handleListDragOver}
          onDrop={handleListDrop}
        >
        {view.sections.map((s, i) => (
          <Fragment key={i}>
            <Indicator slot={i} />
            <li
              draggable
              onDragStart={(e) => {
                setDragIndex(i);
                e.dataTransfer.effectAllowed = 'move';
                // Firefox needs setData to actually start the drag.
                try {
                  e.dataTransfer.setData('text/plain', String(i));
                } catch {
                  /* ignore */
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                // Decide before/after based on cursor position relative
                // to the card's vertical midpoint.
                const rect = e.currentTarget.getBoundingClientRect();
                const above = e.clientY < rect.top + rect.height / 2;
                const slot = above ? i : i + 1;
                if (insertIndex !== slot) setInsertIndex(slot);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setInsertIndex(null);
              }}
              className={`border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-800 space-y-2 transition ${
                dragIndex === i ? 'opacity-40' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing select-none"
                  title="Drag to reorder"
                >
                  ⋮⋮
                </span>
                <span className="text-xs font-mono text-gray-400 dark:text-gray-500">§{i + 1}</span>
                <input
                  type="text"
                  placeholder="Section heading"
                  value={s.heading || ''}
                  onChange={(e) => updateSection(i, { heading: e.target.value })}
                  className="flex-1 font-medium text-gray-900 dark:text-gray-100 border-0 border-b border-gray-100 dark:border-gray-700 focus:outline-none focus:border-blue-400 dark:focus:border-blue-700 px-0 py-1"
                />
                <input
                  type="number"
                  min={50}
                  step={10}
                  value={s.target_words ?? ''}
                  onChange={(e) =>
                    updateSection(i, {
                      target_words: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  className="w-20 text-xs text-right border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  title="Min words for this section"
                />
                <span className="text-xs text-gray-400 dark:text-gray-500">w</span>
                <button
                  type="button"
                  onClick={() => deleteSection(i)}
                  className="icon-btn text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                  title="Delete section"
                >
                  ✕
                </button>
              </div>
              <input
                type="text"
                placeholder="One-sentence angle / focus for this section"
                value={s.angle || ''}
                onChange={(e) => updateSection(i, { angle: e.target.value })}
                className="w-full text-sm italic text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <label className="block">
                <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Key points (separate with ` | `)
                </span>
                <textarea
                  rows={3}
                  placeholder="point a | point b | point c"
                  value={joinList(s.key_points)}
                  onChange={(e) => updateSection(i, { key_points: splitList(e.target.value) })}
                  className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Facts to cite (separate with ` | `)
                </span>
                <textarea
                  rows={2}
                  placeholder="fact one [1] | fact two [2]"
                  value={joinList(s.facts)}
                  onChange={(e) => updateSection(i, { facts: splitList(e.target.value) })}
                  className="mt-0.5 w-full text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            </li>
          </Fragment>
        ))}
        {/* Trailing slot — drop after the last card. */}
        <Indicator slot={view.sections.length} />
        </ol>
        );
      })()}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addSection}
          className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          + Add section
        </button>
        <div className="flex-1" />
        {error && (
          <span className="text-xs text-red-600 dark:text-red-400 mr-2">{error}</span>
        )}
        {dirty && (
          <button
            type="button"
            onClick={handleRevert}
            disabled={saving}
            className="text-sm px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
          >
            Revert
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : dirty ? 'Save outline' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

function WritePanel({
  content,
  title,
  description,
}: {
  content?: string | null;
  title?: string | null;
  description?: string | null;
}) {
  if (!content) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        Body not written yet. The agent produces this in the write phase.
      </div>
    );
  }
  const wordCount = content.split(/\s+/).filter(Boolean).length;
  return (
    <div className="space-y-2">
      {(title || description) && (
        <div className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-800">
          {title && <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</div>}
          {description && <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{description}</div>}
        </div>
      )}
      <div className="text-xs text-gray-500 dark:text-gray-400">{wordCount.toLocaleString()} words</div>
      <pre className="text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-[600px] overflow-auto">
        {content}
      </pre>
    </div>
  );
}

/**
 * FinalPanel — read-mostly view of the rendered article. Shows the user
 * what the page will look like once it ships: title (h1), description
 * (subtitle), slug (mono caption), body (markdown rendered). Clicking
 * any field switches it to edit mode in place; blur or Enter saves via
 * api.updateManagedPage. There's no agent button or chat input here —
 * any model-driven changes belong on the Write tab.
 *
 * Body editing toggles between rendered preview and an MDEditor instance
 * (same component the legacy edit page uses), since clicking inside long
 * markdown to "start editing" everywhere isn't practical.
 */
type HeaderField = 'title' | 'description' | 'slug';

// One Save / Cancel bar for the whole header card. Committing on blur alone
// left people unsure whether anything happened — and for the slug it was
// actively misleading, since the value redisplayed unchanged. `onMouseDown`
// rather than `onClick` so the action fires before an input's blur can tear
// the buttons down under the cursor.
function EditActions({
  onSave,
  onCancel,
  saving,
  dirty,
}: {
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  dirty: boolean;
}) {
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          onSave();
        }}
        disabled={saving || !dirty}
        className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          onCancel();
        }}
        disabled={saving}
        className="px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
      >
        Cancel
      </button>
      {dirty && !saving && (
        <span className="text-[11px] text-amber-600 dark:text-amber-400">
          unsaved
        </span>
      )}
    </div>
  );
}

function FinalPanel({
  title,
  description,
  slug,
  slugSource,
  pathSegments,
  managedSiteId,
  managedSiteType,
  lastDeployedAt,
  content,
  keyword,
  pageId,
  versionId,
  onSaved,
}: {
  title?: string | null;
  description?: string | null;
  slug?: string | null;
  // "user" (pinned — builds leave it alone), "build", or null (derived from
  // the keyword). Drives the provenance hint under the URL.
  slugSource?: string | null;
  // The sections above the slug. Without these the "URL" line was the last
  // segment of the URL presented as the whole of it.
  pathSegments?: string[];
  managedSiteId?: number | null;
  managedSiteType?: string | null;
  lastDeployedAt?: string | null;
  content?: string | null;
  keyword?: string | null;
  pageId: number;
  versionId?: number | null;
  onSaved: () => void;
}) {
  // The markdown editor/preview (@uiw) themes off its own `data-color-mode` attribute, not
  // Tailwind's .dark class — hard-coded to "light" it rendered dark text on the dark card.
  const { theme } = useTheme();
  // Which header fields are open for editing. A SET, not a single field:
  // clicking a second field used to close the first, so the Save button
  // hopped around and each field committed as its own PATCH — meaning its own
  // content version. Now all three can be open together and one Save commits
  // them in a single request, cutting one version.
  const [editingFields, setEditingFields] = useState<Set<HeaderField>>(
    () => new Set()
  );
  const isEditing = (f: HeaderField) => editingFields.has(f);
  const openField = (f: HeaderField) =>
    setEditingFields((prev) => new Set(prev).add(f));
  const [bodyEditing, setBodyEditing] = useState(false);
  // Structured editor (citations as protected chips) by default; "raw" falls
  // back to the plain markdown editor on the clean body.
  const [bodyMode, setBodyMode] = useState<'structured' | 'raw'>('structured');
  // Reading, not editing. "Rendered" is the writer's copy as stored;
  // "Published" asks the server for what the DEPLOY will commit, which is a
  // different string the moment this site shows its sources.
  const [bodyView, setBodyView] = useState<'rendered' | 'published'>('rendered');

  // Local drafts kept separate from server values so blur-to-save can
  // commit cleanly. Initialised lazily from props each time the user
  // enters edit mode for that field.
  const [titleDraft, setTitleDraft] = useState(title || '');
  const [descDraft, setDescDraft] = useState(description || '');
  const [slugDraft, setSlugDraft] = useState(slug || '');
  // The path is edited separately from the header card: it is a different
  // PATCH (page-level, not version-level) and a much rarer act, so folding it
  // into the shared Save would make every title edit look like a page move.
  const [editingPath, setEditingPath] = useState(false);
  const [pathDraft, setPathDraft] = useState<string[]>(pathSegments ?? []);
  const [savingPath, setSavingPath] = useState(false);
  const nested = isNestedSiteType(managedSiteType);
  const pathPrefix = pathSegments ?? [];
  const pathDirty = pathDraft.join('/') !== pathPrefix.join('/');
  const navigateFromCard = useNavigate();
  // Read this page where it will be READ: inside the site it deploys into.
  // The URL line says where the page lands; this opens it there, in the same
  // shell the Hugo layouts are compiled from. Only for a site we generate —
  // on a WordPress target the theme is the customer's and there is nothing of
  // ours to render.
  // With a trail, so the preview's exit comes back to the build rather than
  // dropping you in the site designer.
  const sitePreviewHref =
    managedSiteId && nested && slug
      ? withReturnTo(
          `/deliver/${managedSiteId}/design/preview?path=` +
            encodeURIComponent(previewPath(pathPrefix, slug, true)),
          `/page-builder/${pageId}`,
          'the page builder',
        )
      : null;

  const savePath = async () => {
    setSavingPath(true);
    try {
      await api.updateManagedPage(pageId, { path_segments: pathDraft });
      setEditingPath(false);
      onSaved();
    } finally {
      setSavingPath(false);
    }
  };
  const [bodyDraft, setBodyDraft] = useState(content || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync drafts when the upstream content changes (e.g. after a write
  // re-run completes), but only when the user isn't mid-edit on that
  // field — otherwise we'd clobber their typing.
  useEffect(() => {
    if (!editingFields.has('title')) setTitleDraft(title || '');
  }, [title, editingFields]);
  useEffect(() => {
    if (!editingFields.has('description')) setDescDraft(description || '');
  }, [description, editingFields]);
  useEffect(() => {
    if (!editingFields.has('slug')) setSlugDraft(slug || '');
  }, [slug, editingFields]);

  // Follow the server once a save lands, but never yank the field out from
  // under someone mid-edit — same rule as the slug draft above.
  useEffect(() => {
    if (!editingPath) setPathDraft(pathSegments ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(pathSegments ?? []).join('/'), editingPath]);
  useEffect(() => {
    if (!bodyEditing) setBodyDraft(content || '');
  }, [content, bodyEditing]);

  const persist = async (patch: Record<string, string>) => {
    setSaving(true);
    setError(null);
    try {
      await api.updateManagedPage(pageId, patch);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const drafts: Record<HeaderField, string> = {
    title: titleDraft,
    description: descDraft,
    slug: slugDraft,
  };
  const originals: Record<HeaderField, string> = {
    title: title || '',
    description: description || '',
    slug: slug || '',
  };
  const dirtyFields = (Object.keys(drafts) as HeaderField[]).filter(
    (f) => drafts[f] !== originals[f]
  );

  // One PATCH for everything that changed. Sending them separately cut a
  // content version per field — three edits, three versions, for what the
  // user experienced as one edit.
  const saveHeader = async () => {
    if (dirtyFields.length) {
      const patch: Record<string, string> = {};
      for (const f of dirtyFields) patch[f] = drafts[f];
      await persist(patch);
    }
    setEditingFields(new Set());
  };

  const cancelHeader = () => {
    setTitleDraft(title || '');
    setDescDraft(description || '');
    setSlugDraft(slug || '');
    setEditingFields(new Set());
  };

  const commitBody = async () => {
    setBodyEditing(false);
    if (bodyDraft !== (content || '')) {
      await persist({ content: bodyDraft });
    }
  };

  // Structured editor save: the markdown carries <cite> tags, so the backend
  // rebuilds structured_content and provenance survives the edit.
  const commitStructuredBody = async (markdown: string) => {
    await persist({ content: markdown });
    setBodyEditing(false);
  };

  if (!content && !title && !description) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        Nothing to render yet - run the write phase (or build the page
        end-to-end) to populate the final article.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header card — title / description / slug. Each is click-to-edit. */}
      <article className="border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 p-5 sm:p-6 space-y-3 shadow-sm">
        {/* Title */}
        {isEditing('title') ? (
          <input
            autoFocus
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveHeader();
              } else if (e.key === 'Escape') {
                cancelHeader();
              }
            }}
            className="w-full text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 border-b-2 border-blue-300 dark:border-blue-700 focus:outline-none focus:border-blue-500 px-0 py-1 bg-transparent"
          />
        ) : (
          <h1
            onClick={() => {
              setTitleDraft(title || '');
              openField('title');
            }}
            className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/30 -mx-1 px-1 rounded"
            title="Click to edit title"
          >
            {title || <span className="text-gray-400 dark:text-gray-500 italic">(no title)</span>}
          </h1>
        )}

        {/* Description */}
        {isEditing('description') ? (
          <textarea
            autoFocus
            rows={2}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancelHeader();
            }}
            className="w-full text-base text-gray-600 dark:text-gray-300 border-b border-blue-300 dark:border-blue-700 focus:outline-none focus:border-blue-500 px-0 py-1 bg-transparent resize-none"
          />
        ) : (
          <p
            onClick={() => {
              setDescDraft(description || '');
              openField('description');
            }}
            className="text-base text-gray-600 dark:text-gray-300 cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/30 -mx-1 px-1 rounded"
            title="Click to edit description"
          >
            {description || (
              <span className="text-gray-400 dark:text-gray-500 italic">(no description)</span>
            )}
          </p>
        )}

        {/* Slug */}
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <span className="font-medium uppercase tracking-wide">URL:</span>
            {/* THE SECTIONS, which used to be missing entirely. A page under
                two sections was shown as `best-tires` and published at
                `/bikes/trails/best-tires/` — the line called itself URL and
                was one segment of it. Dimmed because the slug is the editable
                part here; the sections open below. */}
            {nested && pathPrefix.length > 0 && (
              <button
                type="button"
                onClick={() => setEditingPath((v) => !v)}
                title="Edit the publish path"
                className="font-mono text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 px-1 rounded shrink-0"
              >
                /{pathPrefix.join('/')}
              </button>
            )}
            {isEditing('slug') ? (
              <input
                autoFocus
                type="text"
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveHeader();
                  } else if (e.key === 'Escape') {
                    cancelHeader();
                  }
                }}
                className="font-mono text-gray-700 dark:text-gray-300 border-b border-blue-300 dark:border-blue-700 focus:outline-none focus:border-blue-500 px-1 py-0.5 bg-transparent flex-1"
              />
            ) : (
              <code
                onClick={() => {
                  setSlugDraft(slug || '');
                  openField('slug');
                }}
                className="font-mono text-gray-700 dark:text-gray-300 cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/30 px-1 rounded"
                title="Click to edit URL slug"
              >
                {slug || <span className="text-gray-400 dark:text-gray-500 italic">(no slug)</span>}
              </code>
            )}
          </div>
          {/* The sections, editable in place. Collapsed by default: the path
              is right far more often than not, and an always-open editor
              would put the least-changed part of the URL above the most.

              NOT gated on `nested`. A flat target (WordPress, Manual) still
              STORES the path — it files the page in the site structure and it
              is what a later move to a nested target would publish under. The
              first version hid the editor on flat sites, which made the path
              unreachable for anyone whose sites are all Manual. PathChips says
              plainly that the URL does not use it. */}
          {(
            <div className="mt-1.5">
              {editingPath ? (
                <div className="max-w-xl">
                  <PathChips
                    segments={pathDraft}
                    onChange={setPathDraft}
                    managedSiteId={managedSiteId ?? null}
                    previewSlug={slug || 'page-slug'}
                    nested={nested}
                  />
                  <div className="mt-1.5 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={savePath}
                      disabled={savingPath || !pathDirty}
                      className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingPath ? 'Saving…' : 'Save path'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPathDraft(pathSegments ?? []);
                        setEditingPath(false);
                      }}
                      className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                    {lastDeployedAt && pathDirty && (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400">
                        Published - moving it breaks links to the old URL.
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                (pathPrefix.length === 0 || !nested) && (
                  <button
                    type="button"
                    onClick={() => setEditingPath(true)}
                    className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {pathPrefix.length
                      ? `Publish path: /${pathPrefix.join('/')}/`
                      : '+ Add a publish path'}
                  </button>
                )
              )}
            </div>
          )}

          {/* Where the URL came from. Worth stating: a build will overwrite a
              generated slug but never a pinned one, and changing a slug after
              deploy breaks inbound links to the old one. */}
          <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
            {slugSource === 'user'
              ? 'Pinned by you - re-running the build won’t change it. Clear it to hand the choice back.'
              : slugSource === 'build'
                ? 'Set by the build. Editing it pins your version.'
                : 'Derived from the keyword. The build will set one, or you can pin your own.'}
          </p>
        </div>

        {/* One Save for the whole card — title, description and slug commit
            together in a single PATCH (one content version), so the button
            doesn't hop between fields as you click around. */}
        {(editingFields.size > 0 || dirtyFields.length > 0) && (
          <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
            <EditActions
              onSave={saveHeader}
              onCancel={cancelHeader}
              saving={saving}
              dirty={dirtyFields.length > 0}
            />
          </div>
        )}
      </article>

      {/* Body */}
      <article className="border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Body
          </span>
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            {content && (
              <span>
                {content.split(/\s+/).filter(Boolean).length.toLocaleString()} words
              </span>
            )}
            {bodyEditing ? (
              <>
                {/* Structured ↔ raw markdown toggle. */}
                <div className="flex overflow-hidden rounded border border-gray-300 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => setBodyMode('structured')}
                    className={`px-2 py-0.5 ${
                      bodyMode === 'structured'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Structured
                  </button>
                  <button
                    type="button"
                    onClick={() => setBodyMode('raw')}
                    className={`px-2 py-0.5 ${
                      bodyMode === 'raw'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Raw
                  </button>
                </div>
                {/* Structured mode has its own Save/Cancel inside the editor;
                    raw mode uses these header buttons. */}
                {bodyMode === 'raw' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setBodyDraft(content || '');
                        setBodyEditing(false);
                      }}
                      disabled={saving}
                      className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={commitBody}
                      disabled={saving || bodyDraft === (content || '')}
                      className="px-2 py-1 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : 'Save body'}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                {/* Stored copy ↔ what the deploy will commit. */}
                {content && versionId && (
                  <div className="flex overflow-hidden rounded border border-gray-300 dark:border-gray-600">
                    {(['rendered', 'published'] as const).map((view) => (
                      <button
                        key={view}
                        type="button"
                        onClick={() => setBodyView(view)}
                        title={
                          view === 'rendered'
                            ? 'The body as it is stored.'
                            : 'The body as it will be published, sources and all.'
                        }
                        className={`px-2 py-0.5 capitalize ${
                          bodyView === view
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                )}
                {/* The third way to look at the body: not as source and not
                    as the string that gets committed, but as a reader meets it
                    — inside the site's own layout, type and navigation. */}
                {sitePreviewHref && (
                  <button
                    type="button"
                    onClick={() => navigateFromCard(sitePreviewHref)}
                    title="Read this page inside the site it deploys into"
                    className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 whitespace-nowrap"
                  >
                    On the site ↗
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setBodyDraft(content || '');
                    setBodyMode('structured');
                    setBodyEditing(true);
                  }}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  ✎ Edit body
                </button>
              </>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {bodyEditing && bodyMode === 'structured' ? (
            versionId ? (
              <StructuredBodyEditor
                pageId={pageId}
                versionId={versionId}
                onSave={commitStructuredBody}
                onCancel={() => setBodyEditing(false)}
              />
            ) : (
              <div className="text-sm text-gray-400 dark:text-gray-500 italic">
                Build the page before editing structurally, or switch to Raw.
              </div>
            )
          ) : bodyEditing ? (
            <div data-color-mode={theme}>
              <MDEditor
                value={bodyDraft}
                onChange={(val) => setBodyDraft(val || '')}
                preview="edit"
                height={600}
                visibleDragbar
              />
            </div>
          ) : content && bodyView === 'published' && versionId ? (
            <PublishedBodyPreview pageId={pageId} versionId={versionId} />
          ) : content ? (
            <div data-color-mode={theme} className="prose dark:prose-invert prose-sm max-w-none overflow-x-auto break-words">
              <MDEditor.Markdown
                source={content}
                style={{ background: 'transparent' }}
              />
            </div>
          ) : (
            <div className="text-sm text-gray-400 dark:text-gray-500 italic">
              No body yet. Click "Edit body" to start writing manually, or run
              the write phase.
            </div>
          )}
        </div>
      </article>

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Tip: click the title, description, or URL slug to edit in place. Use
        the "Edit body" button to switch between rendered preview and the
        markdown editor.
      </p>

      {/* The SEO scorecard moved to the SEO phase panel, where it's obviously
          checking-and-editing (see PageBuilderWorkflow's active === 'seo'). */}

      {/* Citation provenance — every cited claim, its bound source fact, and
          whether the source supports it (from the structured document). */}
      <ProvenancePanel pageId={pageId} versionId={versionId} />
    </div>
  );
}

// Inline word-level diff: removed words struck through in red, added in green,
// unchanged in normal ink. Reads at a glance for short fields (title, meta,
// heading, an edited sentence) — the whole point of the SEO correction list.
const InlineWordDiff = memo(function InlineWordDiff({
  before,
  after,
}: {
  before: string;
  after: string;
}) {
  // Memoized: for a section correction these are whole rendered sections, and
  // diffWords is O(n·d) — without this it re-runs on every parent render (the
  // build-state poll, every websocket event) while the SEO tab is open.
  const parts = useMemo(() => diffWords(before || '', after || ''), [before, after]);
  return (
    <span className="text-sm leading-relaxed break-words">
      {parts.map((p, i) =>
        p.added ? (
          <span
            key={i}
            className="rounded-sm bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
          >
            {p.value}
          </span>
        ) : p.removed ? (
          <span
            key={i}
            className="rounded-sm bg-red-100 text-red-700 line-through decoration-red-400 dark:bg-red-900/30 dark:text-red-300"
          >
            {p.value}
          </span>
        ) : (
          <span key={i} className="text-gray-700 dark:text-gray-300">
            {p.value}
          </span>
        ),
      )}
    </span>
  );
});

const SeoCorrectionRow = memo(function SeoCorrectionRow({
  c,
}: {
  c: import('../types').SEOCorrection;
}) {
  const hasBefore = c.before != null && c.before !== '';
  return (
    <div className="border-b border-gray-100 py-2 last:border-0 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {c.target.replace(/_/g, ' ')}
        </span>
        {c.reason && (
          <span className="truncate text-xs text-gray-400 dark:text-gray-500">{c.reason}</span>
        )}
      </div>
      <div className="mt-1">
        {hasBefore ? (
          <InlineWordDiff before={c.before as string} after={c.after || ''} />
        ) : (
          // Newly added value (no prior) — show it entirely as an addition.
          <span className="rounded-sm bg-green-100 text-sm text-green-800 break-words dark:bg-green-900/40 dark:text-green-300">
            {c.after}
          </span>
        )}
      </div>
    </div>
  );
});

function SeoPanel({
  seoReport,
  running,
}: {
  seoReport?: BuildStateResponse['seo_report'];
  running?: boolean;
}) {
  if (!seoReport) {
    return (
      <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {running
          ? 'Optimizing SEO - correcting title, meta, and keyword placement…'
          : 'No SEO pass yet. Run the build (or the SEO phase) to auto-correct on-page SEO on the structured document.'}
      </div>
    );
  }
  const corrections = seoReport.corrections || [];
  const suggestions = seoReport.suggestions || [];
  const remaining = (seoReport.checks_after || []).filter(
    (c) => c.status === 'problem' || c.status === 'warning',
  );
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">SEO corrections</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Auto-fixes to the structured document, each verified by an eval before it was committed.
          </p>
        </div>
        <div className="text-sm tabular-nums text-gray-600 dark:text-gray-300">
          Score {seoReport.score_before ?? '–'}{' '}
          <span aria-hidden>→</span>{' '}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {seoReport.score_after ?? '–'}
          </span>
        </div>
      </div>

      {corrections.length > 0 ? (
        <>
          <div className="text-[10px] font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
            Applied ({corrections.length})
          </div>
          {corrections.map((c, i) => (
            <SeoCorrectionRow key={`c-${i}`} c={c} />
          ))}
        </>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          No corrections were committed{suggestions.length ? '' : ' - SEO already looks good.'}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
            Suggestions - not applied (failed eval) ({suggestions.length})
          </div>
          {suggestions.map((s, i) => (
            <SeoCorrectionRow key={`s-${i}`} c={s} />
          ))}
        </div>
      )}

      {remaining.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Remaining (advisory) ({remaining.length})
          </div>
          {remaining.map((c) => (
            <div key={c.id} className="py-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">{c.title}:</span>{' '}
              {c.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroundPanel({
  report,
  groundingFlags,
  onFix,
  fixing,
  running,
  pageId,
  versionId,
}: {
  report?: BuildStateResponse['compliance_report'];
  // What the fact-check found: {flags: [{claim, issue}], approved, checked_at}.
  // Separate from `report`, which is the deterministic spec check (word
  // counts, section bounds) — two different questions about the same draft.
  groundingFlags?: BuildStateResponse['grounding_flags'];
  onFix: () => void;
  fixing: boolean;
  // The ground phase is executing right now. The report only lands at the
  // end, so without this the panel showed "No compliance check yet" for the
  // entire run — a phase visibly working read as finished-and-empty.
  running?: boolean;
  pageId: number;
  versionId?: number | null;
}) {
  const flags = (groundingFlags?.flags || []) as Array<{
    claim?: string;
    issue?: string;
  }>;
  const checked = !!groundingFlags?.checked_at;
  const faithfulness = groundingFlags?.faithfulness;
  const revisedSections = groundingFlags?.revised_sections ?? 0;
  const revisedFromVersionId = groundingFlags?.revised_from_version_id ?? null;
  // undefined on runs that predate the flag — only `false` means blind.
  const citationsVisible = groundingFlags?.citations_visible;

  // Every cited claim with its bound source and the verifier's verdict. The
  // same table the Final page shows — belongs here too, since "which claims
  // were checked and did they pass" is the question this phase answers.
  const provenance = (
    <CollapsibleSection title="Claims checked" defaultOpen={!flags.length}>
      <ProvenancePanel pageId={pageId} versionId={versionId ?? undefined} />
    </CollapsibleSection>
  );

  const factCheck = checked ? (
    <div className="space-y-2">
      <div
        className={`px-3 py-2 rounded text-sm font-medium ${
          flags.length === 0
            ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
            : 'bg-rose-50 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300'
        }`}
      >
        {flags.length === 0
          ? 'Fact-check passed - no unsupported claims found'
          : `${flags.length} unsupported claim(s)`}
      </div>
      {/* The phase had no structured document and re-parsed citation-stripped
          prose, so nothing could be checked against its source. Said out loud
          because otherwise "no unsupported claims" and a zero verdict count
          look like a clean bill of health rather than a check that couldn't
          see anything. */}
      {citationsVisible === false && (
        <div className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          <strong>Ran without citations.</strong> This version has no structured
          document, so the check read the plain article text - citation markers
          had already been stripped. Claims could not be verified against their
          sources, and every claim looked uncited. Re-run <em>Write</em> to
          rebuild the structured document, then run Ground again.
        </div>
      )}
      {/* Per-citation entailment counts: of the claims that DO cite a source,
          how many does that source actually support. A different question from
          the flags above, which are about claims with no support at all. */}
      {faithfulness && citationsVisible !== false && (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {typeof faithfulness.SUPPORTED === 'number' && (
            <span className="text-emerald-700 dark:text-emerald-400">
              {faithfulness.SUPPORTED} cited claim(s) verified against their source
            </span>
          )}
          {!!faithfulness.NOT_SUPPORTED && (
            <span className="text-rose-700 dark:text-rose-400 font-medium">
              {faithfulness.NOT_SUPPORTED} cited claim(s) NOT supported by the
              source they point at
            </span>
          )}
        </div>
      )}
      {flags.length > 0 && (
        <>
          <ul className="space-y-1.5 text-sm">
            {flags.map((f, i) => (
              <li
                key={i}
                className="border border-rose-200 dark:border-rose-800 rounded p-2 bg-rose-50/40 dark:bg-rose-900/20"
              >
                <div className="text-rose-900 dark:text-rose-200">
                  &ldquo;{f.claim}&rdquo;
                </div>
                {f.issue && (
                  <div className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                    {f.issue}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {/* Whether the draft was actually changed. Ground now runs the
              same section-scoped revision the full build does, so saying
              "reported, not fixed" would be wrong — but a run that revised
              nothing must not imply it did. */}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {revisedSections
              ? `${revisedSections} section(s) were rewritten to resolve these - saved as a new version, so the previous draft is still recoverable.`
              : 'Nothing was rewritten: these claims could not be located in a single section, or the reviser left them as-is. The draft is unchanged.'}
          </p>
          {/* The revision cut a new version, so both sides exist — show the
              actual change rather than asserting one happened. */}
          {revisedSections && revisedFromVersionId && versionId ? (
            <VersionDiff
              pageId={pageId}
              fromVersionId={revisedFromVersionId}
              toVersionId={versionId}
              label={`See what grounding changed (${revisedSections} section${revisedSections === 1 ? '' : 's'})`}
            />
          ) : null}
        </>
      )}
    </div>
  ) : null;

  if (!report && running) {
    return (
      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        Fact-checking the draft - claims are verified against the research, so
        this takes a while on a long page. The report appears when it finishes.
      </div>
    );
  }
  if (!report) {
    return (
      <div className="space-y-3">
        {factCheck}
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No spec check yet. Runs after the write phase.
        </div>
        {versionId ? provenance : null}
      </div>
    );
  }
  const passed = !!report.passed;
  const violations = (report.violations || []) as Array<{
    field?: string;
    severity?: string;
    expected?: unknown;
    actual?: unknown;
    message?: string;
  }>;
  return (
    <div className="space-y-3">
      {factCheck}
      <div
        className={`px-3 py-2 rounded text-sm font-medium ${
          passed ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
        }`}
      >
        {passed ? 'Spec compliance passed' : `${violations.length} spec violation(s)`}
      </div>
      {violations.length > 0 && (
        <div>
          <button
            type="button"
            onClick={onFix}
            disabled={fixing}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {fixing ? 'Fixing…' : 'Fix violations with AI'}
          </button>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Rewrites the draft to satisfy the spec, then re-checks compliance.
          </p>
        </div>
      )}
      {violations.length > 0 && (
        <ul className="space-y-1.5 text-sm">
          {violations.map((v, i) => (
            <li
              key={i}
              className="border border-amber-200 dark:border-amber-800 rounded p-2 bg-amber-50/40 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide">
                <span className="font-medium">{v.field || 'rule'}</span>
                {v.severity && <span className="text-amber-700 dark:text-amber-300">{v.severity}</span>}
              </div>
              <div className="mt-1">{v.message || JSON.stringify(v)}</div>
            </li>
          ))}
        </ul>
      )}
      {versionId ? provenance : null}
    </div>
  );
}

export default function PageBuilderWorkflow() {
  const { pageId: pageIdParam } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageId = Number(pageIdParam);
  const { subscribe } = useWebSocket();
  const networkEnabled = useNetworkEnabled();
  const showInternals = useShowInternals();

  const [state, setState] = useState<BuildStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<BuildPhase>('research');
  // Which phase is currently mid-run (set on `<phase>_started`, cleared on
  // `<phase>_completed` / `failed`). Drives the spinner + disables run buttons.
  const [runningPhase, setRunningPhase] = useState<BuildPhase | null>(null);
  // Same value, readable from websocket handlers. Those subscribe once (deps
  // are [pageId, subscribe]) so the state variable is captured stale in their
  // closure; anything that needs "what is running RIGHT NOW" mid-event reads
  // this instead.
  const runningPhaseRef = useRef<BuildPhase | null>(null);
  useEffect(() => {
    runningPhaseRef.current = runningPhase;
  }, [runningPhase]);
  // NOTE: there is deliberately no auto-advance state here. Running a phase
  // leaves you on that phase so you can read what it produced — navigating
  // away the instant the artifact lands is the opposite of what a
  // review-each-step builder is for. Next is a separate, explicit press.

  // Panels holding unsaved local edits register a flush here, keyed by the
  // phase they belong to; they deregister when clean. Next awaits it before
  // navigating, so leaving a phase persists your edits instead of dropping
  // them when the panel unmounts. A ref, not state — registering must not
  // re-render the page.
  const pendingSaves = useRef<Map<BuildPhase, () => Promise<void>>>(new Map());
  const registerPendingSave = useCallback(
    (phase: BuildPhase, save: (() => Promise<void>) | null) => {
      if (save) pendingSaves.current.set(phase, save);
      else pendingSaves.current.delete(phase);
    },
    []
  );
  // Per-phase binders. Stable identities — the panels list these in effect
  // deps, so a new function each render would re-register on every keystroke.
  const registerOutlineSave = useCallback(
    (save: (() => Promise<void>) | null) => registerPendingSave('outline', save),
    [registerPendingSave]
  );
  const registerAngleSave = useCallback(
    (save: (() => Promise<void>) | null) => registerPendingSave('angle', save),
    [registerPendingSave]
  );
  const registerResearchSave = useCallback(
    (save: (() => Promise<void>) | null) =>
      registerPendingSave('research', save),
    [registerPendingSave]
  );
  // True only while Next is flushing a panel save, so the button can say so.
  const [savingBeforeNext, setSavingBeforeNext] = useState(false);

  // A phase WE just enqueued that the worker hasn't picked up yet. Bridges
  // the gap between "job queued" and "current_phase stamped" so a
  // build-state refresh landing in between doesn't clear the spinner.
  // Cleared once the server reports it running, or on any terminal event.
  const queuedRunRef = useRef<{ phase: BuildPhase; at: number } | null>(null);

  // How long to keep believing a queued run that the server hasn't confirmed.
  // Covers RQ pickup latency (usually sub-second, longer when workers are
  // busy) while staying bounded: a job that dies before emitting any event
  // must not leave the spinner up and every Run button disabled forever.
  const QUEUED_RUN_GRACE_MS = 60_000;

  const takeQueuedRun = (): BuildPhase | null => {
    const q = queuedRunRef.current;
    if (!q) return null;
    if (Date.now() - q.at > QUEUED_RUN_GRACE_MS) {
      queuedRunRef.current = null;
      return null;
    }
    return q.phase;
  };
  // True while an end-to-end build is in flight. The full build emits bare
  // `started` / `completed` / `failed` (no phase prefix) on the websocket,
  // distinct from the per-phase `<phase>_started` events. Disables every
  // per-phase Re-run button so the user can't fight the worker.
  const [fullBuildRunning, setFullBuildRunning] = useState(false);
  // True between clicking Cancel and the worker actually exiting. The
  // backend's cancel endpoint just stamps cancel_requested_at; the
  // worker only stops at the next iteration / phase boundary, so the
  // UI shows "Cancelling…" until the websocket reports a terminal
  // status (cancelled / completed / failed).
  const [cancelling, setCancelling] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'error'; text: string } | null>(
    null,
  );
  // Whether the SERP slideout (same component used on the Topic page) is
  // open. Lets the user click around the live SERP data to verify the
  // angle / outline without leaving the page builder.
  const [serpOpen, setSerpOpen] = useState(false);
  // Whether the slide-out "Concierge" chat pane is open. The concierge
  // agent orchestrates the build phases; it talks to the same build the
  // stepper reflects, so the chat is purely additive UI.
  const [chatOpen, setChatOpen] = useState(false);
  // Latest SERP collected for this page's keyword — drives the header
  // trigger's engine-icon + "1w ago" label, matching the Topic page.
  const [latestSerp, setLatestSerp] = useState<{
    engine: string | null;
    date: string | null;
  } | null>(null);
  // A SERP crawl is in flight for this keyword (request accepted, results
  // not yet back). Set when we POST a request or hear `serp_requested`,
  // cleared on `serp_completion`. `requestingSerp` covers just the POST.
  const [serpPending, setSerpPending] = useState(false);
  // True when the last Synthesize run finished against cached SERPs but
  // produced no research facts. Drives a "refresh SERPs" nudge in the
  // empty Research panel — the cached SERP pages had nothing extractable,
  // so a fresh crawl is the fix, not another Synthesize.
  const [serpFoundNothing, setSerpFoundNothing] = useState(false);
  const [requestingSerp, setRequestingSerp] = useState(false);
  // Persisting the outline run mode picked from the Run caret menu.
  const [savingOutlineMode, setSavingOutlineMode] = useState(false);

  // SERP-derived research points (Synthesize phase artifact). Populated
  // from /research-points on load and streamed via `research_point_added`
  // websocket events as Synthesize runs.
  const [researchPoints, setResearchPoints] = useState<ResearchPoint[]>([]);
  const [pendingSources, setPendingSources] = useState<PendingSource[]>([]);
  const [pageTypeCounts, setPageTypeCounts] = useState<Record<string, number>>(
    {},
  );

  const refreshResearchPoints = async () => {
    if (!pageId) return;
    try {
      const res = await api.listResearchPoints(pageId);
      setResearchPoints(res.points);
      setPendingSources(res.pending_sources ?? []);
      setPageTypeCounts(res.page_type_counts);
    } catch (e) {
      console.error('Failed to fetch research points', e);
    }
  };

  // Sync the running-state flags from the server's page_status. Lets
  // the Cancel button + spinners appear when the user reloads / navigates
  // back to this page mid-build (the websocket "started" event already
  // fired into the void before this session existed). Conversely, if
  // page_status went terminal while we weren't looking, clear stale
  // flags so the Re-run buttons re-enable.
  // page_status tracks FULL BUILDS only. It must not touch runningPhase:
  // there are two independent running-state fields, and a phased run leaves
  // page_status at whatever the last build left behind (usually DRAFT — the
  // phase jobs never set it). Clearing runningPhase from a stale DRAFT
  // cancelled the spinner on a phase that was actively working.
  // `current_phase` is the authority for phases; see refreshState.
  const _syncRunningFromPageStatus = (status: string | null | undefined) => {
    if (status === 'IN_PROGRESS') {
      setFullBuildRunning((cur) => cur || true);
    } else if (status) {
      setFullBuildRunning(false);
      setCancelling(false);
    }
  };

  const refreshState = async () => {
    try {
      const res = await api.getBuildState(pageId);
      setState(res);
      _syncRunningFromPageStatus(res.page_status);
      // Hydrate the per-phase spinner from the server so a freshly-loaded
      // or second browser reflects WHICH phase is running.
      const server = (res.current_phase ?? null) as BuildPhase | null;
      setRunningPhase(() => {
        if (server) {
          // The worker picked it up — the server is authoritative from here.
          if (queuedRunRef.current?.phase === server) queuedRunRef.current = null;
          return server;
        }
        // Server says nothing is running. That is NOT the same as "the phase
        // you just started isn't running": there's a gap between enqueueing
        // the job and the worker stamping current_phase, and a refresh
        // landing in that gap used to wipe the spinner we'd optimistically
        // set — leaving the run invisible until a full page reload. Keep
        // showing a run we queued ourselves until we see it start or finish.
        return takeQueuedRun();
      });
    } catch (e) {
      console.error('Failed to refresh build state', e);
    }
  };

  // Pull the most-recent SERP for this page's keyword so the header
  // trigger can show the engine icon + relative date (same as the Topic
  // page). Re-run when the keyword resolves and after the slideout closes
  // (the user may have fetched a fresh SERP from inside it).
  const refreshLatestSerp = async (keywordId: number) => {
    try {
      const serps = await api.getSerpsByKeyword(keywordId);
      if (serps.length === 0) {
        setLatestSerp(null);
        return;
      }
      const latest = serps.reduce((a, b) =>
        new Date(b.collected_at) > new Date(a.collected_at) ? b : a,
      );
      setLatestSerp({ engine: latest.search_engine, date: latest.collected_at });
    } catch (e) {
      console.error('Failed to fetch latest SERP', e);
    }
  };

  useEffect(() => {
    if (!state?.keyword_id) return;
    refreshLatestSerp(state.keyword_id);
    // Derive the "Pending" state from the backend so it survives a reload
    // (in-memory websocket state is lost on refresh).
    api
      .getSerpPending(state.keyword_id)
      .then((r) => setSerpPending(r.pending))
      .catch(() => {});
  }, [state?.keyword_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kick off a fresh SERP crawl for the keyword (same endpoint the Topic
  // page's "Fetch SERP" link hits). The worker emits `serp_completion`
  // when done, which the subscription below turns into a label refresh.
  const handleRequestSerp = async (keywordId: number, force = false) => {
    setRequestingSerp(true);
    try {
      await api.createSerpRequest(keywordId, force);
      setSerpPending(true);
    } catch (e) {
      console.error('Failed to request SERP', e);
    } finally {
      setRequestingSerp(false);
    }
  };

  // The outline run MODE (project-wide: Project.outline_use_angle_raw). Lives
  // up here rather than in OutlinePanel because it's picked from the Run
  // button's caret menu now — the run it governs is the only place it means
  // anything.
  const handleOutlineModeChange = async (useAngleRaw: boolean) => {
    if (!state?.project_id) return;
    setSavingOutlineMode(true);
    try {
      await api.updateProject(state.project_id, {
        outline_use_angle_raw: useAngleRaw,
      });
      await refreshState();
    } finally {
      setSavingOutlineMode(false);
    }
  };

  // Crawl gate: force a re-crawl + re-extract of the keyword's ranking pages,
  // then auto-re-synthesize. The crawl runs in the background — serpPending +
  // the existing poll / websocket events surface progress and refresh.
  const handleRecrawlResearch = async () => {
    try {
      await api.recrawlResearch(pageId);
      setSerpPending(true);
      setSerpFoundNothing(false);
    } catch (e) {
      console.error('Failed to re-crawl research', e);
    }
  };

  // Cancel a stuck/in-flight crawl: release the pending lock so the trigger
  // becomes clickable again right away. Optimistically clear locally; the
  // backend also broadcasts serp_completion to other open views.
  const handleCancelSerp = async (keywordId: number) => {
    setSerpPending(false);
    try {
      await api.cancelSerpRequest(keywordId);
    } catch (e) {
      console.error('Failed to cancel SERP request', e);
    }
  };

  // Reflect SERP crawl lifecycle in the header trigger without polling.
  useEffect(() => {
    const keywordId = state?.keyword_id;
    if (!keywordId) return;
    return subscribe((event: WebSocketEvent) => {
      if (
        event.event_type === 'serp_requested' &&
        event.keyword_ids.includes(keywordId)
      ) {
        setSerpPending(true);
      } else if (
        event.event_type === 'serp_completion' &&
        event.keyword_ids.includes(keywordId)
      ) {
        setSerpPending(false);
        refreshLatestSerp(keywordId);
      }
    });
  }, [state?.keyword_id, subscribe]); // eslint-disable-line react-hooks/exhaustive-deps

  // While pending, poll the backend so "Pending" clears when the crawl
  // finishes or its request goes stale (worker died) — self-recovers to a
  // clickable trigger without a full page reload.
  useEffect(() => {
    if (!serpPending) return;
    const keywordId = state?.keyword_id;
    if (!keywordId) return;
    const id = setInterval(async () => {
      try {
        const r = await api.getSerpPending(keywordId);
        if (!r.pending) {
          setSerpPending(false);
          refreshLatestSerp(keywordId);
        }
      } catch {
        /* transient — keep polling */
      }
    }, SERP_PENDING_POLL_MS);
    return () => clearInterval(id);
  }, [serpPending, state?.keyword_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pageId) return;
    let live = true;
    setLoading(true);
    Promise.all([api.getBuildState(pageId), api.listResearchPoints(pageId)])
      .then(([res, pts]) => {
        if (!live) return;
        setState(res);
        _syncRunningFromPageStatus(res.page_status);
        // Hydrate the per-phase spinner on initial load so a second
        // browser opened mid-phase shows WHICH phase is running. Same
        // queued-run fallback as refreshState — this effect re-runs on
        // pageId change, and it must not be the one path that still
        // clobbers an optimistic flag with a not-started-yet null.
        setRunningPhase(
          ((res.current_phase ?? null) as BuildPhase | null) ?? takeQueuedRun()
        );
        setResearchPoints(pts.points);
        setPendingSources(pts.pending_sources ?? []);
        setPageTypeCounts(pts.page_type_counts);
        // Land on whatever is happening right now. A running phase wins: if
        // you refresh mid-build, the thing you want to watch is the phase
        // that's working, not the deepest one that finished earlier. Falls
        // back to the deepest completed phase, otherwise stays on research.
        const running = (res.current_phase ?? null) as BuildPhase | null;
        if (running && PHASE_ORDER.includes(running)) {
          setActive(running);
        } else if (res.content && res.content.trim()) {
          // There's a finished article — land on it. `res.phases` only carries
          // the five runnable phases ('final' is appended client-side for the
          // stepper), so the deepest-ready search below can never reach it:
          // a fully built page always landed on Ground, a compliance-report
          // panel, instead of the page you came to look at.
          setActive('final');
        } else {
          const ready = [...res.phases]
            .reverse()
            .find((p) => p.state === 'ready');
          if (ready) setActive(ready.phase);
        }
      })
      .catch((e) => {
        if (live) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [pageId]);

  // Subscribe to per-phase lifecycle events so the UI reflects what the
  // RQ worker is doing without polling. The agent emits
  // "<phase>_started" → "<phase>_completed" or "failed".
  useEffect(() => {
    if (!pageId) return;
    const handler = (event: WebSocketEvent) => {
      if (event.event_type !== 'page_build') return;
      if (!('page_id' in event) || event.page_id !== pageId) return;
      const status = event.status;
      const m = /^([a-z]+)_(started|completed)$/.exec(status);
      if (m) {
        const phase = m[1] as BuildPhase;
        const stage = m[2];
        if (stage === 'started') {
          // Handed off: the worker is running it, so the ref no longer needs
          // to protect the optimistic flag.
          if (queuedRunRef.current?.phase === phase) queuedRunRef.current = null;
          setRunningPhase(phase);
          setFlash({ kind: 'ok', text: event.message || `${phase} started` });
        } else {
          if (queuedRunRef.current?.phase === phase) queuedRunRef.current = null;
          setRunningPhase((cur) => (cur === phase ? null : cur));
          setCancelling(false);
          setFlash({
            kind: 'ok',
            text: event.message || `${phase} done`,
          });
          // Stay on this phase — the user reviews the artifact, then presses
          // Next themselves. refreshState() re-reads phase states so the run
          // button demotes to "re-run" and Next unlocks.
          refreshState();
        }
      } else if (status === 'failed' || status === 'error') {
        queuedRunRef.current = null;
        setRunningPhase(null);
        setFullBuildRunning(false);
        setCancelling(false);
        setFlash({ kind: 'error', text: event.message || 'Build failed' });
        refreshState();
      } else if (status === 'completed') {
        queuedRunRef.current = null;
        setRunningPhase(null);
        setFullBuildRunning(false);
        setCancelling(false);
        setFlash({ kind: 'ok', text: event.message || 'Build completed' });
        refreshState();
      } else if (status === 'cancelled') {
        queuedRunRef.current = null;
        setRunningPhase(null);
        setFullBuildRunning(false);
        setCancelling(false);
        setFlash({
          kind: 'ok',
          text: event.message || 'Build canceled',
        });
        refreshState();
      } else if (status === 'started') {
        // Bare 'started' (no phase prefix) is the end-to-end build kickoff.
        setFullBuildRunning(true);
        setFlash({ kind: 'ok', text: event.message || 'Build started' });
      }
    };
    return subscribe(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, subscribe]);

  // Subscribe to Synthesize-phase events. `research_point_added` streams
  // new rows as the synthesizer explodes each source page's key_points
  // (so the UI fills in live without polling). `synthesize_research`
  // status events drive the runningPhase flag and re-fetch on completion.
  useEffect(() => {
    if (!pageId) return;
    const handler = (event: WebSocketEvent) => {
      if (event.event_type === 'research_point_added') {
        if (event.page_id !== pageId) return;
        // A point arrived — we have facts, so clear any "nothing found" nudge.
        setSerpFoundNothing(false);
        setResearchPoints((cur) => {
          // De-dup on row id in case the row arrives twice (websocket
          // replay after reconnect).
          if (cur.some((p) => p.id === event.point.id)) return cur;
          return [...cur, event.point];
        });
        return;
      }
      if (event.event_type !== 'synthesize_research') return;
      if (event.page_id !== pageId) return;
      // Synthesize runs in TWO roles, and these events look identical in both:
      //   1. AS the Research phase — the standalone job, or the full build's
      //      research step. Then it owns the stepper and the banner.
      //   2. As a SUB-STEP of a downstream phase. Every runner that needs a
      //      research summary calls PhaseInputs.research_summary(), which runs
      //      the synthesizer on demand — so a plain "Re-run write" emits this
      //      whole started→completed cycle partway through the write.
      //
      // Claiming the stepper unconditionally broke case 2: a write re-run
      // flipped to Research mid-flight, then "completed" cleared it — so the
      // panel showed "Run Write" again, as if the run had finished, until the
      // write phase's own event put it back. Only take the stepper when
      // nothing else holds it. The terminal branches below are already
      // guarded (`cur === 'research'`), so they can't clear someone else's.
      const ownsStepper =
        runningPhaseRef.current === null || runningPhaseRef.current === 'research';
      if (event.status === 'started') {
        setSerpFoundNothing(false);
        if (!ownsStepper) return;
        setRunningPhase('research');
        setFlash({
          kind: 'ok',
          text: event.message || 'Synthesize started',
        });
      } else if (event.status === 'progress') {
        // Per-page progress — no flash, just let the rows stream in.
      } else if (event.status === 'completed') {
        setRunningPhase((cur) => (cur === 'research' ? null : cur));
        // Completed means cached SERPs existed (zero-SERP runs report
        // crawl_requested). Zero points added is only a PROBLEM when nothing
        // was deduped either: synthesize is idempotent, so re-running a page
        // that already has all its facts adds zero and skips the lot. Reading
        // added==0 alone told a fully-researched page its SERPs were empty and
        // advised a pointless re-crawl.
        const added = event.points_added ?? 0;
        const skipped = event.points_skipped_existing ?? 0;
        const foundNothing = added === 0 && skipped === 0;
        // Data effects run either way — the research points really did change,
        // and the Research panel should reflect that even when the synthesize
        // was a sub-step of some other phase. Only the BANNER is withheld:
        // "Synthesize complete" mid-write reads as the write having finished.
        setSerpFoundNothing(foundNothing);
        refreshResearchPoints();
        if (!ownsStepper) return;
        setFlash({
          kind: 'ok',
          text:
            event.message ||
            (foundNothing
              ? 'Synthesize found no facts in the cached SERPs - try refreshing SERPs.'
              : added === 0
                ? `Synthesize complete - no new facts; ${skipped} already on the page.`
                : `Synthesize complete - ${added} point(s) added.`),
        });
      } else if (event.status === 'crawl_requested') {
        setRunningPhase((cur) => (cur === 'research' ? null : cur));
        // No cached SERPs — also a zero-fact outcome, so surface the same
        // refresh advice + button rather than only a "crawl queued" toast.
        setSerpFoundNothing(true);
        if (!ownsStepper) return;
        setFlash({
          kind: 'ok',
          text:
            event.message ||
            'No SERPs cached yet - refresh SERPs, then re-run Synthesize.',
        });
      } else if (event.status === 'failed') {
        // Deliberately NOT gated on ownsStepper — a failure the user never
        // sees is worse than one that shows up under the wrong phase.
        setRunningPhase((cur) => (cur === 'research' ? null : cur));
        setFlash({
          kind: 'error',
          text: event.message || 'Synthesize failed',
        });
      }
    };
    return subscribe(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, subscribe]);

  // Angle-phase events. Different shape from synthesize (single-shot
  // LLM call, no per-row streaming) so it gets its own handler.
  useEffect(() => {
    if (!pageId) return;
    const handler = (event: WebSocketEvent) => {
      if (event.event_type !== 'angle_generated') return;
      if (event.page_id !== pageId) return;
      if (event.status === 'started') {
        setRunningPhase('angle');
        setFlash({
          kind: 'ok',
          text: event.message || 'Generating angle…',
        });
      } else if (event.status === 'completed') {
        setRunningPhase((cur) => (cur === 'angle' ? null : cur));
        setFlash({
          kind: 'ok',
          text: event.message || 'Angle ready.',
        });
        // Angle hydrates framing + research_evidence — refresh the full
        // build state so those panels update too.
        refreshState();
      } else if (event.status === 'failed') {
        setRunningPhase((cur) => (cur === 'angle' ? null : cur));
        setFlash({
          kind: 'error',
          text: event.message || 'Angle generation failed.',
        });
      }
    };
    return subscribe(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, subscribe]);

  // Reconciliation poll. Websocket events drive the running flags during a
  // normal session, but events get missed — a reload eats the `started`, a
  // reconnect eats the `completed`.
  //
  // This runs UNCONDITIONALLY, which is the point. It used to be gated on
  // `runningPhase || fullBuildRunning`, so the poll existed only while the UI
  // already believed something was running — meaning any single spurious
  // clear was PERMANENT: the flags went false, the interval tore itself down,
  // and nothing ever re-checked. A phase would keep working while the page
  // sat there claiming it was idle, recoverable only by a manual refresh.
  //
  // `current_phase` is durable and authoritative, so a poll can always
  // correct a wrong belief in either direction. Idle polling is slower to
  // keep it cheap.
  useEffect(() => {
    if (!pageId) return;
    const busy = !!runningPhase || fullBuildRunning;
    const id = setInterval(() => {
      refreshState();
    }, busy ? 10_000 : 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, runningPhase, fullBuildRunning]);

  // Request cooperative cancel. The backend just stamps
  // cancel_requested_at; the worker exits at the next iteration / phase
  // boundary. Until the websocket reports a terminal status we keep the
  // running spinners up but flip the button to "Cancelling…".
  const handleCancel = async () => {
    if (!runningPhase && !fullBuildRunning) return;
    if (cancelling) return;
    setCancelling(true);
    setFlash({ kind: 'ok', text: 'Cancel requested - waiting for worker…' });
    try {
      await api.cancelManagedPageBuild(pageId);
    } catch (e) {
      setCancelling(false);
      setFlash({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Cancel request failed',
      });
    }
  };

  // Kick off the end-to-end build (research → framing → outline → write →
  // ground → save). Lifecycle is driven by the same `started` / `completed`
  // / `failed` websocket events run_build_page_job already emits — the
  // handler above flips fullBuildRunning back to false on either terminal
  // event and refreshes the build state so each phase card reflects what
  // ran.
  const handleBuildAll = async () => {
    if (runningPhase || fullBuildRunning) return;
    setFullBuildRunning(true);
    setFlash({ kind: 'ok', text: 'Queuing end-to-end build…' });
    try {
      const res = await api.buildManagedPage(pageId);
      setFlash({ kind: 'ok', text: res.message || 'End-to-end build queued' });
    } catch (e) {
      setFullBuildRunning(false);
      setFlash({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Failed to queue build',
      });
    }
  };

  // Wipe a phase's accumulated state so the next Re-run starts fresh
  // (instead of appending, which is now the default for research). The
  // backend's _PHASE_RESET_CONFIG details what each phase clears.
  // Confirmation is window.confirm — keeping it dumb until we have a
  // proper modal helper; the destructive action is loud in the
  // returned message anyway.
  const handleResetPhase = async (phase: BuildPhase) => {
    if (phase === 'final') return;
    if (runningPhase || fullBuildRunning) return;
    const ok = window.confirm(
      `Reset the ${phase} phase? This wipes the ${phase}-phase conversation ` +
        `and any artifacts it produced. Downstream phases will become stale.`,
    );
    if (!ok) return;
    try {
      const res = await api.resetManagedPagePhase(pageId, phase as
        | 'research'
        | 'angle'
        | 'outline'
        | 'write'
        | 'ground');
      setFlash({ kind: 'ok', text: res.message || `${phase} reset` });
      refreshState();
      // The research points list lives in its own state (not build-state), so
      // a research reset must update it too. Optimistically drop the wiped rows
      // immediately (reset spares manual sources, mirroring the backend) so the
      // UI updates without a full-page refresh even if the reconciling re-fetch
      // is slow or errors; then reconcile from the server.
      if (phase === 'research') {
        setSerpFoundNothing(false);
        setResearchPoints((cur) => cur.filter((p) => p.is_manual));
        refreshResearchPoints();
      }
    } catch (e) {
      setFlash({
        kind: 'error',
        text: e instanceof Error ? e.message : `Failed to reset ${phase}`,
      });
    }
  };

  const handleRunPhase = async (phase: BuildPhase, feedback?: string) => {
    // 'final' is a UI-only phase — there's no agent run for it. Guard
    // here so accidental wiring (e.g. a header button on the wrong
    // phase) doesn't try to enqueue an unknown phase on the backend.
    if (phase === 'final') return;
    if (runningPhase || fullBuildRunning) return;
    // Optimistic, plus a ref so a build-state refresh can't undo it before
    // the worker stamps current_phase (see refreshState).
    queuedRunRef.current = { phase, at: Date.now() };
    setRunningPhase(phase);
    setFlash({
      kind: 'ok',
      text: feedback
        ? `Queuing ${phase} with feedback…`
        : `Queuing ${phase}…`,
    });
    try {
      const res = await api.runPhase(pageId, phase, feedback);
      setFlash({
        kind: 'ok',
        text: res.message || `${phase} queued`,
      });
    } catch (e) {
      queuedRunRef.current = null;
      setRunningPhase(null);
      setFlash({
        kind: 'error',
        text: e instanceof Error ? e.message : `Failed to queue ${phase}`,
      });
    }
  };

  // Whether Next is allowed to leave `phase` — i.e. whether the phase has
  // produced anything for the next one to consume.
  //
  // Gated on "has output", NOT on "is fresh": `stale` counts. A stale
  // artifact still exists and may carry hand edits, so requiring a re-run to
  // move past it would both trap the user and silently regenerate work they
  // did by hand. Stale is a warning (amber run button + badge), not a wall.
  //
  // The rule is "is there anything on this screen": if the panel is showing
  // rows, Next works. We check the rendered artifact FIRST and fall back to
  // the server's phase state, rather than trusting phase state alone — it is
  // derived separately and can under-report (Research is fed by two
  // independent producers, and the phase-state calc long counted only one),
  // and a user looking at a full page does not care which producer filled it.
  const phaseHasOutput = (phase: BuildPhase): boolean => {
    if (phase === 'final') return false;
    if (phase === 'research') {
      if (researchPoints.length > 0) return true;
      if ((state?.research_evidence?.length ?? 0) > 0) return true;
    }
    const status = state?.phases.find((p) => p.phase === phase);
    return status?.state === 'ready' || status?.state === 'stale';
  };

  // Next — pure forward navigation, never runs anything. Running is the run
  // button's job; keeping the two separate means Next can't spend tokens by
  // surprise and can't clobber a stale artifact. "Build end-to-end" remains
  // the one-press path for anyone who doesn't want to step through.
  //
  // It does persist, though: each phase panel is conditionally rendered, so
  // moving on UNMOUNTS it and would drop any unsaved draft with it. Panels
  // with local edits register a flush above; Next runs it first. If the save
  // fails we stay put rather than navigating away from work we couldn't
  // persist.
  const handleNext = async () => {
    if (runningPhase || fullBuildRunning) return;
    if (!phaseHasOutput(active)) return;
    const next = nextPhase(active);
    if (!next) return;

    const flush = pendingSaves.current.get(active);
    if (flush) {
      setSavingBeforeNext(true);
      try {
        await flush();
      } catch (e) {
        setFlash({
          kind: 'error',
          text:
            e instanceof Error
              ? `Couldn't save your ${PHASE_LABEL[active]} changes: ${e.message}`
              : `Couldn't save your ${PHASE_LABEL[active]} changes - staying here.`,
        });
        return;
      } finally {
        setSavingBeforeNext(false);
      }
    }
    setActive(next);
  };

  // Ground panel's "Fix violations" button. Enqueues an LLM rewrite that
  // clears spec violations. Backend reuses the ground-phase lifecycle
  // events, so the ground spinner + state refresh are driven by the same
  // websocket handler as a normal phase re-run.
  const handleFixCompliance = async () => {
    if (runningPhase || fullBuildRunning) return;
    setRunningPhase('ground');
    setFlash({ kind: 'ok', text: 'Queuing violation fix…' });
    try {
      const res = await api.fixCompliance(pageId);
      setFlash({ kind: 'ok', text: res.message || 'Compliance fix queued' });
    } catch (e) {
      setRunningPhase(null);
      setFlash({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Failed to queue compliance fix',
      });
    }
  };

  const handleFramingSaved = (next: FramingArtifact) => {
    setState((s) => (s ? { ...s, framing: next } : s));
    setFlash({ kind: 'ok', text: 'Framing saved' });
  };

  if (!pageId) return <div className="p-6 text-sm text-red-600 dark:text-red-400">Missing page id.</div>;
  if (loading) return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  if (error)
    return (
      <div className="p-6 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error}
      </div>
    );
  if (!state) return null;

  // 'final' is a UI-only step: there's no agent run behind it, it's the
  // rendered article. That made it tempting to call it ready whenever content
  // exists — but the rendered article is only as fresh as the phases that
  // produced it, so re-running Angle used to leave Write and Ground correctly
  // yellow while Final sat green, claiming the output was current when it was
  // built from an angle that had since changed. Any stale producer upstream
  // makes the render stale too.
  const finalStatus: PhaseStatus = {
    phase: 'final' as const,
    state: (state.content
      ? state.phases.some((p) => p.state === 'stale')
        ? 'stale'
        : 'ready'
      : 'pending') as PhaseStatus['state'],
    summary: state.content
      ? `${state.content.split(/\s+/).filter(Boolean).length.toLocaleString()} words rendered`
      : null,
  };

  return (
    <div className="flex gap-0">
      <SiblingPagesSidebar
        currentPageId={pageId}
        topicId={state.topic_id ?? null}
        // Carries the trail across: arriving from Trends and flicking to a
        // sibling page should not silently strand you — the way back is still
        // the same way back.
        buildHref={(siblingId) =>
          `/page-builder/${siblingId}${
            searchParams.get('from')
              ? `?${keepReturnTo(searchParams, new URLSearchParams()).toString()}`
              : ''
          }`
        }
      />
      <div className="flex-1 min-w-0 max-w-7xl mx-auto p-4 sm:p-6">
      {(() => {
        // THE TRAIL IS THE WAY OUT, so it leads where you came IN from.
        //
        // It used to be the subject / section / node / page chain, from when
        // the editor lived under Subjects. The editor is an ARTIFACT now — a page
        // carries its own publish path and the subject only suggested it once
        // — and a trail whose every rung jumped to Subjects left anyone who
        // arrived via Artifacts with no route back to a list of artifacts at all.
        //
        // The research chain is not dropped, it is DEMOTED: it says where this
        // page came from, which is real and worth a link, but it is provenance
        // rather than position. It renders under the trail, not in it.
        const items: BreadcrumbItem[] = [
          { label: 'Pages', to: '/pages' },
          {
            label: state.keyword || `page ${pageId}`,
            isCurrent: true,
          },
        ];
        // Models widget rides the right end of the breadcrumb row: collapsed to
        // a count by default, expanding to every stage this screen can bill.
        // The backend owns that stage list (stage_surfaces."page_builder"), so
        // it stays honest when an agent here picks up a new stage.
        return (
          <Breadcrumb
            items={items}
            right={
              <>
                {/* THE WAY OUT RIDES THE BREADCRUMB ROW, right end — the one
                    placement every screen uses now. It was a chip on its own
                    line above this, which in a full-height editor (no page card,
                    its own container, a sibling sidebar to the left) put it at a
                    different x-offset from every other screen in the app.

                    Distinct from the breadcrumb beside it: that goes to /pages,
                    which answers "where does this page live", not "back to what
                    I was doing". */}
                <ReturnTrail className="mb-0" />
                <StageModelsWidget
                  surface="page_builder"
                  returnLabel="page builder"
                />
              </>
            }
          />
        );
      })()}
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className={WORKSPACE_TITLE_CLASS}>
            Page builder · {state.keyword || `page ${pageId}`}
          </h1>
          {/* WHERE THIS PAGE CAME FROM, and only that. It was the breadcrumb
              until the editor moved under Artifacts; as a trail it was actively
              wrong, because clicking any rung left the section you were in.
              As provenance it is still worth having and still worth linking —
              it is the fastest way back to the research behind a page.

              Deliberately not the publish path: the two are allowed to differ
              now, and the publish path has its own editor on the Final tab. */}
          {(state.project_id || state.topic_name) && (
            <div
              className="text-xs text-gray-400 dark:text-gray-500 mt-0.5"
              title="The subject this page came from - click any part to go back to it"
            >
              <span>From </span>
              {state.project_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/subjects/${state.project_id}`)}
                  className="underline decoration-dotted underline-offset-2 hover:text-blue-600 dark:hover:text-blue-400 hover:decoration-solid"
                >
                  {state.project_name || 'subject'}
                </button>
              )}
              {state.category_id && (
                <>
                  <span className="mx-1">/</span>
                  <button
                    type="button"
                    // A PLAIN node id. `category_id` carries the parent NODE's
                    // id since migration 108, not a legacy category id — the
                    // `category-` spelling would look up an id that does not
                    // exist and select nothing.
                    onClick={() =>
                      navigate(`/subjects/${state.project_id}?node=${state.category_id}`)
                    }
                    className="underline decoration-dotted underline-offset-2 hover:text-blue-600 dark:hover:text-blue-400 hover:decoration-solid"
                  >
                    {state.category_name || 'section'}
                  </button>
                </>
              )}
              {state.topic_id && (
                <>
                  <span className="mx-1">/</span>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/subjects/${state.project_id}?node=${state.topic_id}`)
                    }
                    className="underline decoration-dotted underline-offset-2 hover:text-blue-600 dark:hover:text-blue-400 hover:decoration-solid"
                  >
                    {state.topic_name || 'node'}
                  </button>
                </>
              )}
            </div>
          )}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Status: {state.page_status || '(none)'}
            {runningPhase && (
              <span className="ml-2 text-blue-700 dark:text-blue-300">
                · running {runningPhase}…
              </span>
            )}
            {fullBuildRunning && !runningPhase && (
              <span className="ml-2 text-blue-700 dark:text-blue-300">
                · running end-to-end build…
              </span>
            )}
          </div>
        </div>
        {/* Actions: just the button row now. The lone GENERATE_ARTICLE chip that
            used to sit under it moved into the breadcrumb models widget, which
            shows that stage alongside the six others this screen bills — the
            concierge, its compactor, the research extractor, the image pair. */}
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            {/* View SERPs — opens the same slideout used on the Topic page,
                keyed by this page's primary keyword. Lets the user click
                around the live SERP data to sanity-check the angle / outline
                without navigating away. Hidden when we have no keyword_id. */}
            {state.keyword_id &&
              (latestSerp?.date ? (
                <button
                  onClick={() => setSerpOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 whitespace-nowrap"
                  title="Open the live SERP results for this keyword to verify the angle and outline."
                >
                  <EngineIcon engine={latestSerp.engine} size="sm" />
                  <span>{formatSerpDate(latestSerp.date)}</span>
                </button>
              ) : serpPending ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">
                  <RefreshGlyph spinning />
                  <span>Pending</span>
                  <button
                    onClick={() => handleCancelSerp(state.keyword_id!)}
                    className="icon-btn ml-0.5 text-amber-500 hover:text-red-600 dark:hover:text-red-400"
                    title="Cancel this SERP crawl"
                    aria-label="Cancel SERP crawl"
                  >
                    ✕
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => handleRequestSerp(state.keyword_id!)}
                  disabled={requestingSerp || !networkEnabled}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  title={
                    networkEnabled
                      ? 'Crawl the live SERP for this keyword so you can verify the angle and outline against it.'
                      : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'
                  }
                >
                  {requestingSerp ? '…' : 'Fetch SERP'}
                </button>
              ))}
            {/* Single toggle button: when nothing is running it kicks off
                an end-to-end build; while a build is running it cancels
                the in-flight job. Cooperative cancel — the worker exits at
                the next iteration / phase boundary, then emits a
                `cancelled` websocket event that flips the running flag
                off. Same surface area whether you triggered the run from
                this session or are catching up to one already in flight
                (we sync fullBuildRunning from page_status on load). */}
            {(runningPhase || fullBuildRunning) ? (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50 whitespace-nowrap"
                title="Stop the in-flight build at the next safe break point. Already-completed phases are kept."
              >
                {cancelling ? 'Canceling…' : 'Cancel current build'}
              </button>
            ) : (
              <button
                onClick={handleBuildAll}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 whitespace-nowrap"
                title="Run every phase end-to-end (research → framing → outline → write → ground)."
              >
                Build end-to-end
              </button>
            )}
            {/* Ask AI — opens the slide-out chat where you can talk to the
                builder. The agent orchestrates the same build phases the
                stepper shows, triggering re-runs asynchronously. This is the
                ONLY entry point: the per-phase hand-off panel that used to sit
                under every phase was redundant with it. */}
            <button
              onClick={() => setChatOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded whitespace-nowrap"
              title="Ask AI to research, reframe, re-outline, rewrite, or fact-check this page. It reads the current phase's artifact first, then re-runs the phase with your instruction."
            >
              {/* Sparkle, not a chat bubble — the sparkle is this app's
                  "this calls the LLM" mark (Quick Suggest, Discover
                  Keywords), and "Ask AI" already says it opens a chat. */}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
              Ask AI
            </button>
            {/* Gear icon — navigates to the page-builder settings route, the
                same pattern used on Projects / Categories / Topics. */}
            <Link
              to={`/page-builder/${pageId}/settings`}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
              title="Page settings"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* WHAT THIS BUILD IS WORKING FROM. The trend, the linked room, the
          subject's rules, the research and the documents all already reached
          the writer — but the only way to learn that was to run a build and
          read the chat transcript, because each one lived on a different
          screen. Sits under the provenance line because it answers the same
          question one step further on: not just where this page came from, but
          what comes with it. */}
      <BuildContextStrip pageId={pageId} />

      {flash && (
        <div
          className={`mb-3 text-sm px-3 py-1.5 rounded border ${
            flash.kind === 'ok'
              ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
          }`}
        >
          {flash.text}
        </div>
      )}

      {(() => null)()}
      {/* Append a synthesized 'final' status so the stepper can render
          a sixth step. Backend doesn't know about this phase — it's a
          UI-only review surface — so we compute readiness from whether
          the page has body content, and staleness from the phases that
          PRODUCED that content. See `finalStatus`. */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] gap-4">
        {/* Stepper */}
        <aside>
          <PhaseStepper
            phases={[...state.phases, finalStatus]}
            active={active}
            onSelect={setActive}
            runningPhase={runningPhase}
            runningSince={state.current_phase_started_at}
            runtimes={state.phase_runtimes}
          />
          {/* The page claims a phase is running but the row has gone quiet
              for longer than a live worker ever does — almost always a worker
              crash, container restart, or deploy mid-build. Left stamped, it
              shows a permanent spinner and disables every Run button, with
              nothing on screen explaining why. */}
          {state.current_phase_stalled && (
            <div className="mt-3 rounded border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
              <div className="text-xs font-medium text-rose-800 dark:text-rose-300">
                {PHASE_LABEL[(state.current_phase || 'write') as BuildPhase]} looks
                stuck
              </div>
              <p className="mt-1 text-[11px] text-rose-700 dark:text-rose-400">
                {showInternals ? (
                  <>
                    No sign of the worker for{' '}
                    {formatSeconds(state.current_phase_silent_seconds || 0)}. It
                    was probably killed mid-run (restart or deploy).
                  </>
                ) : (
                  <>
                    It has been silent for{' '}
                    {formatSeconds(state.current_phase_silent_seconds || 0)} and
                    has probably stopped.
                  </>
                )}{' '}
                Clearing lets you run again - anything it already saved is kept.
              </p>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="mt-2 px-3 py-1 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded disabled:opacity-50"
              >
                {cancelling ? 'Clearing…' : 'Clear stuck state'}
              </button>
            </div>
          )}
        </aside>

        {/* Active phase panel */}
        <main>
          {(() => {
            // Same synthesized status the stepper uses, so the panel header's
            // stale banner agrees with the step's colour.
            const allPhases = [...state.phases, finalStatus];
            const activeStatus = allPhases.find((p) => p.phase === active);
            const activeState = activeStatus?.state ?? 'pending';
            const isStale = activeState === 'stale';
            // No agent action on 'final' — it's a review surface.
            const showHeaderRerun = active !== 'final';

            const isRunningActive = runningPhase === active;
            const canStopActive = STOPPABLE_PHASES.includes(active);
            // Same predicate handleNext guards on, so the button's disabled
            // state and the handler can't disagree.
            const hasOutput = phaseHasOutput(active);
            const nextTarget = nextPhase(active);
            const nextDisabled =
              !hasOutput ||
              !nextTarget ||
              savingBeforeNext ||
              runningPhase !== null ||
              fullBuildRunning;

            // Run-button variants for this phase, folded into its caret menu.
            // Research escalates (cheap local extract → go back out to the
            // engine); Outline picks the mode the run will use. Everything
            // else has exactly one way to run and gets a plain button.
            const runMenuItems: RunMenuItem[] = [];
            let runChip: ReactNode = null;
            let caretAttention = false;
            if (active === 'research' && state.keyword_id) {
              const readiness = state.crawl_readiness;
              const broken =
                (readiness?.failed ?? 0) > 0 || (readiness?.pending ?? 0) > 0;
              // Unconditional, unlike the old in-panel button, which only
              // rendered when a page had failed or was still pending — so a
              // keyword whose pages all extracted cleanly but yielded nothing
              // useful had no way to go get fresh ones.
              caretAttention = broken || serpFoundNothing;
              runMenuItems.push({
                key: 'recrawl',
                label: 'Re-fetch SERP & re-extract',
                badge: 'slow · whole keyword',
                tone: 'warn',
                disabled: serpPending || runningPhase !== null || fullBuildRunning,
                onSelect: handleRecrawlResearch,
                blurb:
                  'Goes back out to the search engine: fresh SERP, re-downloads ' +
                  "every ranking page, re-extracts, then re-runs Extract facts " +
                  'automatically. Affects every managed page on this keyword.',
              });
            }
            if (active === 'outline' && state.project_id) {
              runChip = (
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide bg-black/10 dark:bg-white/20 rounded px-1.5 py-0.5">
                  {state.outline_use_angle_raw ? 'direct' : 'AI'}
                </span>
              );
              runMenuItems.push(
                {
                  key: 'outline-llm',
                  label: 'Write the outline with AI',
                  selected: !state.outline_use_angle_raw,
                  disabled: savingOutlineMode,
                  onSelect: () => handleOutlineModeChange(false),
                  blurb:
                    "The AI writes the outline, anchored to the angle's " +
                    'structure. The default.',
                },
                {
                  key: 'outline-raw',
                  label: "Use the angle's structure raw",
                  badge: 'no AI',
                  selected: !!state.outline_use_angle_raw,
                  disabled: savingOutlineMode,
                  onSelect: () => handleOutlineModeChange(true),
                  blurb:
                    "Projects the angle's grouped key points straight into the " +
                    'outline. Free and instant. Subject-wide setting - applies ' +
                    'to every page in this subject.',
                },
              );
            }

            const nextHint = isRunningActive
              ? undefined
              : isStale
                ? `An upstream phase changed since ${PHASE_LABEL[active]} ran - re-run to refresh it, or continue with what's there.`
                : !hasOutput && nextTarget
                  ? `Run ${PHASE_LABEL[active]} to unlock ${PHASE_LABEL[nextTarget]}.`
                  : undefined;
            return (
              <div className="bg-white dark:bg-gray-800 border rounded-lg p-4 mb-3">
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {PHASE_LABEL[active]}
                      </h2>
                      {isStale && (
                        <span
                          className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300"
                          title="An upstream phase changed more recently than this one ran. Re-running is recommended."
                        >
                          stale
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {PHASE_DESCRIPTION[active]}
                    </p>
                  </div>
                  {showHeaderRerun && (
                    <div className="flex flex-col items-end gap-1.5">
                      <div className="flex items-center gap-2">
                        {/* Reset — destructive secondary action paired with
                            Re-run. For research, this is required to wipe;
                            otherwise Re-run APPENDS more turns on top.
                            Other phases reset by clearing their cv/page
                            artifacts (see _PHASE_RESET_CONFIG). */}
                        <button
                          onClick={() => handleResetPhase(active)}
                          disabled={runningPhase !== null || fullBuildRunning}
                          className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50 whitespace-nowrap"
                          title={
                            active === 'research'
                              ? "Wipe research facts + messages. Gathering more APPENDS on top; click Reset first when you want a clean slate."
                              : active === 'ground'
                                ? "Clears the fact-check and spec verdicts (and the per-claim results, so a re-run re-checks everything from scratch). Does NOT undo a grounding revision - that was saved as a content version, so use Version history to roll the text back."
                                : `Wipe the ${active} phase's state (artifacts + conversation). Downstream phases will become stale.`
                          }
                        >
                          Reset {active}
                        </button>
                        {/* Run / Stop toggle. While THIS phase is in flight the
                            run button becomes Stop for the phases the backend
                            can actually interrupt — a cooperative cancel that
                            halts the token spend at the next loop boundary. */}
                        {isRunningActive && canStopActive ? (
                          <button
                            onClick={handleCancel}
                            disabled={cancelling}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50 whitespace-nowrap"
                            title="Stop this phase at the next safe break point. Work already saved is kept; the rest is skipped."
                          >
                            {cancelling ? 'Stopping…' : `Stop ${PHASE_LABEL[active]}`}
                          </button>
                        ) : (
                          <PhaseRunSplitButton
                            onClick={() => handleRunPhase(active)}
                            // A blocked phase has no input to work from, so
                            // running it could only fail — and for the header
                            // image it would fail after spending money. The
                            // backend refuses it too; this just stops the user
                            // finding out the hard way.
                            disabled={
                              runningPhase !== null ||
                              fullBuildRunning ||
                              !!activeStatus?.blocked_reason
                            }
                            themeClass={runButtonTheme(activeState)}
                            label={
                              isRunningActive
                                ? `Running ${active}…`
                                : runButtonLabel(active, activeState)
                            }
                            chip={isRunningActive ? null : runChip}
                            icon={
                              isStale && !isRunningActive ? (
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0l-7.1 12.25A2 2 0 004.99 19z" />
                                </svg>
                              ) : null
                            }
                            items={runMenuItems}
                            menuHeading={
                              active === 'outline'
                                ? 'How this run builds the outline'
                                : 'Other ways to run this phase'
                            }
                            caretAttention={caretAttention}
                            caretTitle={
                              active === 'outline'
                                ? 'Choose how the outline is built'
                                : 'Other ways to run this phase'
                            }
                            title={
                              activeStatus?.blocked_reason
                                ? activeStatus.blocked_reason
                                : isStale
                                ? `An upstream phase changed after ${PHASE_LABEL[active]} last ran, so this artifact - and everything downstream - is out of date. Re-run to bring it back in line.`
                                : active === 'research'
                                  ? "Extracts facts from the SERP pages already downloaded - no new crawling. Appends to what's there; Reset first for a clean slate. To re-fetch the pages themselves, use the ▾ menu."
                                  : `Run the ${active} phase, using cached upstream state where possible.`
                            }
                          />
                        )}
                        {/* Next — pure navigation. Locked until this phase has
                            produced something for the next one to consume, so
                            you can't land on Write with nothing to write from.
                            Never runs anything itself. */}
                        <button
                          onClick={handleNext}
                          disabled={nextDisabled}
                          className={`px-3 py-1.5 text-sm font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
                            isStale
                              ? 'text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                              : 'text-white bg-blue-600 hover:bg-blue-700'
                          }`}
                          title={
                            !nextTarget
                              ? 'Last phase.'
                              : hasOutput
                                ? `Move on to ${PHASE_LABEL[nextTarget]}. Nothing runs - press it when you're happy with this phase.`
                                : `Run ${PHASE_LABEL[active]} first - ${PHASE_LABEL[nextTarget]} has nothing to work from yet.`
                          }
                        >
                          {savingBeforeNext
                            ? 'Saving…'
                            : `Next: ${PHASE_LABEL[nextTarget ?? active]} →`}
                        </button>
                      </div>
                      {/* Why Next is locked / why the run button is shouting.
                          A greyed-out button with no explanation is the usual
                          way this pattern goes wrong. */}
                      {nextHint && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 max-w-md text-right">
                          {nextHint}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            {active === 'research' && (
              <ResearchPanel
                pageId={pageId}
                evidence={state.research_evidence}
                researchPoints={researchPoints}
                pendingSources={pendingSources}
                pageTypeCounts={pageTypeCounts}
                injectionMap={state.injection_map}
                running={runningPhase === 'research'}
                onRerun={() => handleRunPhase('research')}
                onPointsChanged={refreshResearchPoints}
                serpFoundNothing={serpFoundNothing}
                crawlReadiness={state.crawl_readiness}
                onRecrawl={state.keyword_id ? handleRecrawlResearch : undefined}
                recrawling={serpPending}
                onRegisterSave={registerResearchSave}
              />
            )}
            {active === 'angle' && (
              <AnglePanel
                pageId={pageId}
                angle={state.angle}
                framing={state.framing}
                running={runningPhase === 'angle'}
                onSaved={refreshState}
                onFramingSaved={handleFramingSaved}
                onRegisterFramingSave={registerAngleSave}
                optionCount={state.angle_option_count ?? 0}
                effectivePageStyle={state.page_style}
                pageStyleSource={state.page_style_source}
              />
            )}
            {active === 'image' && <HeroImagePanel pageId={pageId} />}
            {active === 'outline' && (
              <OutlinePanel
                outline={state.outline}
                pageId={pageId}
                onSaved={refreshState}
                onRegisterSave={registerOutlineSave}
              />
            )}
            {active === 'write' && (
              <WritePanel
                content={state.content}
                title={state.title}
                description={state.description}
              />
            )}
            {active === 'ground' && (
              <div className="space-y-4">
                <GroundPanel
                  report={state.compliance_report}
                  groundingFlags={state.grounding_flags}
                  onFix={handleFixCompliance}
                  fixing={runningPhase === 'ground' || fullBuildRunning}
                  running={runningPhase === 'ground' || fullBuildRunning}
                  pageId={pageId}
                  versionId={state.current_content_version_id}
                />
                <StageDiff pageId={pageId} fromStage="write" toStage="ground" />
              </div>
            )}
            {active === 'seo' && (
              <div className="space-y-4">
                <SeoPanel
                  seoReport={state.seo_report}
                  running={runningPhase === 'seo' || fullBuildRunning}
                />
                <StageDiff pageId={pageId} fromStage="ground" toStage="seo" />
                {/* The live SEO scorecard against the current content — re-runnable
                    right where the phase edits, so re-checking shows the improved
                    result the phase produced. */}
                {state.keyword &&
                  (state.title || state.description || state.content || state.slug) && (
                    <article className="rounded-md border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                      <SEOPanel
                        keyword={state.keyword}
                        title={state.title || ''}
                        description={state.description || ''}
                        content={state.content || ''}
                        slug={state.slug || ''}
                      />
                    </article>
                  )}
              </div>
            )}
            {active === 'final' && (
              <FinalPanel
                title={state.title}
                description={state.description}
                slug={state.slug}
                slugSource={state.slug_source}
                pathSegments={state.path_segments}
                managedSiteId={state.managed_site_id ?? null}
                managedSiteType={state.managed_site_type ?? null}
                lastDeployedAt={state.last_deployed_at ?? null}
                content={state.content}
                keyword={state.keyword}
                pageId={pageId}
                versionId={state.current_content_version_id}
                onSaved={refreshState}
              />
            )}
          </div>
        </main>
      </div>
      </div>

      {serpOpen && state.keyword_id && (
        <SerpModal
          isOpen={true}
          onClose={() => {
            setSerpOpen(false);
            if (state.keyword_id) refreshLatestSerp(state.keyword_id);
          }}
          keywordId={state.keyword_id}
          keywordValue={state.keyword || ''}
          topicKeywordId={state.topic_keyword_id ?? undefined}
        />
      )}

      <PageBuilderChatPane
        pageId={pageId}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
}
